import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createPokerStudyBoard,
  createPokerStudySpotFlopDecisionPoint,
  createPokerStudyResponseDecisionPointFromAction,
  createPokerStudySpotStrategyBoardFamily,
  createPokerStudySpotStrategyLine,
  createPokerStudySpotStrategyLineAction,
  deletePokerStudyBoard,
  deletePokerStudySpotStrategyLine,
  getPokerStudySpotDetail,
  getPokerStudySpotStrategy,
  listPokerStudyPreflopRangeTemplates,
  listPokerStudySpotPreflopRanges,
  updatePokerStudyBoard,
  updatePokerStudySpotStrategyLine,
  upsertPokerStudySpotNotes,
  upsertPokerStudySpotPreflopRange,
} from '../services/pokerStudyService';
import type {
  PokerStudyBoardFilterPreset,
  PokerStudyBoardFilterPresetConfig,
  PokerStudyPreflopRangeTemplate,
  PokerStudySpotDecisionPointWorkspace,
  PokerStudySpotDetail,
  PokerStudySpotPreflopRange,
  PokerStudySpotStrategy,
  PokerStudySpotStrategyLineWorkspace,
} from '../types/pokerStudy';

type SpotNotesAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type TheoryPanelMode = 'free_notes' | 'strategy_map';
type BoardDialogMode = 'create' | 'edit';
type BoardTypeDialogMode = 'create' | 'edit';

type StrategyLineFormState = {
  name: string;
  strategy_type: string;
  summary: string;
  notes: string;
};

type StrategyLineActionFormState = {
  action_type: 'check' | 'bet' | 'call' | 'raise' | 'fold' | 'other';
  size_percentage: string;
  label: string;
  weight_label: string;
  notes: string;
};

type StrategyBoardFamilyFormState = {
  title: string;
  description: string;
  examples_text: string;
  tags_text: string;
  notes: string;
};

type BoardFilterPresetFormState = {
  label: string;
  high_card_rank: string;
  paired_status: string;
  broadway_count: string;
  suited_texture: string;
  connectivity_texture: string;
  straight_possible: string;
};

type BoardFormState = {
  board_mode: string;
  flop_card_1: string;
  flop_card_2: string;
  flop_card_3: string;
  board_family_label: string;
  board_tags_text: string;
};

const INITIAL_BOARD_FORM: BoardFormState = {
  board_mode: 'exact_board',
  flop_card_1: '',
  flop_card_2: '',
  flop_card_3: '',
  board_family_label: '',
  board_tags_text: '',
};

const INITIAL_STRATEGY_LINE_FORM: StrategyLineFormState = {
  name: '',
  strategy_type: 'general',
  summary: '',
  notes: '',
};

const INITIAL_STRATEGY_LINE_ACTION_FORM: StrategyLineActionFormState = {
  action_type: 'bet',
  size_percentage: '25',
  label: '',
  weight_label: '',
  notes: '',
};

const INITIAL_BOARD_FILTER_PRESET_FORM: BoardFilterPresetFormState = {
  label: '',
  high_card_rank: 'all',
  paired_status: 'all',
  broadway_count: 'all',
  suited_texture: 'all',
  connectivity_texture: 'all',
  straight_possible: 'all',
};

const INITIAL_STRATEGY_BOARD_FAMILY_FORM: StrategyBoardFamilyFormState = {
  title: '',
  description: '',
  examples_text: '',
  tags_text: '',
  notes: '',
};

const ROOT_STRATEGY_LINE_PRESETS: Array<{
  label: string;
  strategy_type: string;
  summary: string;
}> = [
  {
    label: 'Bet range',
    strategy_type: 'range_bet',
    summary: 'Línea para boards donde el agresor mantiene ventaja clara de rango.',
  },
  {
    label: 'Mixto',
    strategy_type: 'mixed',
    summary: 'Línea con varias acciones posibles según composición, suits o adaptación.',
  },
  {
    label: 'Check range',
    strategy_type: 'range_check',
    summary: 'Línea para boards donde la estrategia principal pasa por checkear.',
  },
];

const RESPONSE_VS_AGGRESSION_LINE_PRESETS: Array<{
  label: string;
  strategy_type: string;
  summary: string;
}> = [
  {
    label: 'Raise',
    strategy_type: 'raise',
    summary: 'Línea de respuesta agresiva frente a la apuesta rival.',
  },
  {
    label: 'Call',
    strategy_type: 'call',
    summary: 'Línea de defensa pagando frente a la apuesta rival.',
  },
  {
    label: 'Fold',
    strategy_type: 'fold',
    summary: 'Línea de abandono frente a la apuesta rival.',
  },
  {
    label: 'Mixto',
    strategy_type: 'mixed',
    summary: 'Línea con varias respuestas posibles según blockers, equity o adaptación.',
  },
];

const RESPONSE_VS_CHECK_LINE_PRESETS: Array<{
  label: string;
  strategy_type: string;
  summary: string;
}> = [
  {
    label: 'Bet',
    strategy_type: 'bet',
    summary: 'Línea de apuesta después del check rival.',
  },
  {
    label: 'Check back',
    strategy_type: 'check_back',
    summary: 'Línea de check back para realizar equity o proteger rango.',
  },
  {
    label: 'Mixto',
    strategy_type: 'mixed',
    summary: 'Línea con mezcla entre apuesta y check back.',
  },
];

function getStrategyLinePresetsForDecisionPoint(
  decisionPoint: PokerStudySpotDecisionPointWorkspace | null,
): Array<{
  label: string;
  strategy_type: string;
  summary: string;
}> {
  if (!decisionPoint?.parent_decision_point_id) {
    return ROOT_STRATEGY_LINE_PRESETS;
  }

  const triggerLabel = (decisionPoint.trigger_action_label || '').trim().toLowerCase();

  if (triggerLabel.includes('check')) {
    return RESPONSE_VS_CHECK_LINE_PRESETS;
  }

  return RESPONSE_VS_AGGRESSION_LINE_PRESETS;
}

const STRATEGY_ACTION_PRESETS: Array<{
  label: string;
  action_type: StrategyLineActionFormState['action_type'];
  size_percentage: string;
}> = [
  { label: 'Check', action_type: 'check', size_percentage: '' },
  { label: 'Bet 25', action_type: 'bet', size_percentage: '25' },
  { label: 'Bet 33', action_type: 'bet', size_percentage: '33' },
  { label: 'Bet 50', action_type: 'bet', size_percentage: '50' },
  { label: 'Bet 75', action_type: 'bet', size_percentage: '75' },
  { label: 'Bet 100', action_type: 'bet', size_percentage: '100' },
];

const BOARD_FAMILY_PRESETS: Array<{
  title: string;
  tags_text: string;
  description: string;
}> = [
  {
    title: '1 broadway rainbow',
    tags_text: 'broadway, one-broadway, rainbow',
    description: 'Boards con una broadway y textura rainbow.',
  },
  {
    title: '2 broadways',
    tags_text: 'broadway, two-broadway',
    description: 'Boards con dos cartas broadway, con o sin proyecto de color.',
  },
  {
    title: 'Board doblado alto',
    tags_text: 'paired, high-pair',
    description: 'Boards doblados altos, por ejemplo JJx+.',
  },
  {
    title: 'Board doblado medio',
    tags_text: 'paired, middle-pair',
    description: 'Boards doblados medios, por ejemplo TTx o 99x.',
  },
  {
    title: 'Board doblado bajo',
    tags_text: 'paired, low-pair',
    description: 'Boards doblados bajos, por ejemplo 88x o inferiores.',
  },
  {
    title: 'FD board',
    tags_text: 'flush-draw, two-tone',
    description: 'Boards con proyecto de color disponible.',
  },
  {
    title: 'Monocolor',
    tags_text: 'monotone, flush-board',
    description: 'Boards con las tres cartas del mismo palo.',
  },
  {
    title: 'Two-gap',
    tags_text: 'two-gap, disconnected',
    description: 'Boards con separación relevante entre rangos de cartas.',
  },
  {
    title: 'Board bajo',
    tags_text: 'low-board',
    description: 'Boards bajos donde el agresor puede perder ventaja de rango.',
  },
  {
    title: 'Straight possible',
    tags_text: 'straight-possible, connected',
    description: 'Boards donde existen escaleras posibles o mucha conectividad.',
  },
];

const CARD_RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const CARD_SUITS = ['s', 'h', 'd', 'c'];

function buildBoardFormFromBoard(board: PokerStudySpotDetail['boards'][number]): BoardFormState {
  return {
    board_mode: board.board_mode || 'exact_board',
    flop_card_1: board.flop_card_1 || '',
    flop_card_2: board.flop_card_2 || '',
    flop_card_3: board.flop_card_3 || '',
    board_family_label: board.board_family_label || '',
    board_tags_text: board.board_tags.join(', '),
  };
}

function buildBoardPayload(
  form: BoardFormState,
  currentCount: number,
) {
  const tags = form.board_tags_text
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

  if (form.board_mode === 'exact_board') {
    return {
      board_mode: 'exact_board',
      flop_card_1: form.flop_card_1.trim(),
      flop_card_2: form.flop_card_2.trim(),
      flop_card_3: form.flop_card_3.trim(),
      board_family_label: null,
      board_tags: tags,
      display_order: currentCount,
    };
  }

  return {
    board_mode: 'board_family',
    flop_card_1: null,
    flop_card_2: null,
    flop_card_3: null,
    board_family_label: form.board_family_label.trim(),
    board_tags: tags,
    display_order: currentCount,
  };
}

function formatSpotVillainProfileLabel(value: string | null | undefined): string {
  switch ((value || '').trim().toLowerCase()) {
    case 'regular':
      return 'Regular';
    case 'recreational':
      return 'Recreacional';
    case 'unknown':
      return 'Desconocido';
    default:
      return value || 'Sin definir';
  }
}

function buildBoardLabel(
  board: PokerStudySpotDetail['boards'][number],
): string {
  if (board.board_mode === 'exact_board') {
    return `${board.flop_card_1 ?? ''} ${board.flop_card_2 ?? ''} ${board.flop_card_3 ?? ''}`.trim();
  }

  return board.board_family_label || 'Board family';
}

function getBoardBroadwayCount(
  board: PokerStudySpotDetail['boards'][number],
): number | null {
  if (board.broadway_count !== null && board.broadway_count !== undefined) {
    return board.broadway_count;
  }

  switch ((board.broadway_density || '').trim().toLowerCase()) {
    case 'none':
      return 0;
    case 'one':
      return 1;
    case 'two':
      return 2;
    case 'three':
      return 3;
    default:
      return null;
  }
}

function readBoardTypePresetsFromLine(
  line: PokerStudySpotStrategyLineWorkspace,
): PokerStudyBoardFilterPreset[] {
  const rawPresets = line.metadata?.board_type_presets;

  if (!Array.isArray(rawPresets)) {
    return [];
  }

  return rawPresets
    .map((preset) => {
      if (!preset || typeof preset !== 'object') return null;

      const typedPreset = preset as {
        id?: unknown;
        label?: unknown;
        filters?: unknown;
      };

      if (typeof typedPreset.id !== 'string' || typeof typedPreset.label !== 'string') {
        return null;
      }

      const filters =
        typedPreset.filters && typeof typedPreset.filters === 'object'
          ? (typedPreset.filters as PokerStudyBoardFilterPresetConfig)
          : {};

      return {
        id: typedPreset.id,
        label: typedPreset.label,
        filters,
      };
    })
    .filter(Boolean) as PokerStudyBoardFilterPreset[];
}

function buildBoardFilterPresetFormFromPreset(
  preset: PokerStudyBoardFilterPreset,
): BoardFilterPresetFormState {
  return {
    label: preset.label,
    high_card_rank: preset.filters.high_card_rank || 'all',
    paired_status: preset.filters.paired_status || 'all',
    broadway_count: preset.filters.broadway_count || 'all',
    suited_texture: preset.filters.suited_texture || 'all',
    connectivity_texture: preset.filters.connectivity_texture || 'all',
    straight_possible: preset.filters.straight_possible || 'all',
  };
}

function buildBoardTypeTitleFromForm(form: BoardFilterPresetFormState): string {
  const parts: string[] = [];

  if (form.broadway_count !== 'all') {
    parts.push(
      form.broadway_count === '1'
        ? '1 broadway'
        : `${form.broadway_count} broadways`,
    );
  }

  if (form.suited_texture !== 'all') {
    const suitedLabel: Record<string, string> = {
      rainbow: 'rainbow',
      two_tone: 'two-tone',
      monotone: 'monocolor',
    };

    parts.push(suitedLabel[form.suited_texture] || form.suited_texture);
  }

  if (form.paired_status !== 'all') {
    const pairLabel: Record<string, string> = {
      unpaired: 'sin pareja',
      paired: 'doblado',
      trips_board: 'trips',
    };

    parts.push(pairLabel[form.paired_status] || form.paired_status);
  }

  if (form.high_card_rank !== 'all') {
    parts.push(`${form.high_card_rank} high`);
  }

  if (form.connectivity_texture !== 'all') {
    const connectivityLabel: Record<string, string> = {
      connected: 'connected',
      two_gap: 'two-gap',
      one_gap: 'one-gap',
      disconnected: 'disconnected',
    };

    parts.push(connectivityLabel[form.connectivity_texture] || form.connectivity_texture);
  }

  if (form.straight_possible !== 'all') {
    parts.push(form.straight_possible === 'yes' ? 'straight possible' : 'sin straight');
  }

  return parts.length > 0 ? parts.join(' · ') : 'Tipo de board';
}

function readStringFromRecord(
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = source?.[key];

  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readNumberFromRecord(
  source: Record<string, unknown> | null | undefined,
  key: string,
): number | null {
  const value = source?.[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function formatPosition(value: string | null | undefined): string {
  return value ? value.toUpperCase() : '-';
}

function getStreetLabel(value: string | null | undefined): string {
  switch ((value || '').trim().toLowerCase()) {
    case 'flop':
      return 'FLOP';
    case 'turn':
      return 'TURN';
    case 'river':
      return 'RIVER';
    default:
      return value ? value.toUpperCase() : '-';
  }
}

function getStrategyActorLabel(
  spot: PokerStudySpotDetail['spot'],
  actor: string | null | undefined,
): string {
  if (actor === 'hero') {
    return formatPosition(spot.hero_position);
  }

  if (actor === 'villain') {
    return formatPosition(spot.villain_position);
  }

  return '-';
}

function getTheoryActionColor(
  actionType: string,
  sizePercentage?: number | null,
): string {
  const normalizedType = (actionType || '').trim().toLowerCase();

  if (normalizedType === 'fold') {
    return '#3b82f6';
  }

  if (normalizedType === 'check' || normalizedType === 'call') {
    return '#22c55e';
  }

  if (normalizedType === 'bet' || normalizedType === 'raise') {
    const size = Number(sizePercentage ?? 0);

    if (size >= 100) return '#ef4444';
    if (size >= 50) return '#f97316';
    return '#eab308';
  }

  return '#94a3b8';
}

function formatTheoryAssociatedActionLabel(
  actionType: string,
  sizePercentage?: number | null,
): string {
  const normalizedType = (actionType || '').trim().toLowerCase();

  if (normalizedType === 'check') return 'Check';
  if (normalizedType === 'call') return 'Call';
  if (normalizedType === 'fold') return 'Fold';

  if (normalizedType === 'bet') {
    return sizePercentage ? `Bet ${sizePercentage}` : 'Bet';
  }

  if (normalizedType === 'raise') {
    return sizePercentage ? `Raise ${sizePercentage}` : 'Raise';
  }

  if (normalizedType === 'other') return 'Otra';

  return actionType || 'Sin acción';
}

function formatBbAmount(value: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 'No calculado';
  }

  const rounded = Math.round(value * 100) / 100;
  return `${rounded}bb`;
}

function getDeadBlindAmount(positionA: string, positionB: string): number {
  const activePositions = new Set([
    positionA.trim().toLowerCase(),
    positionB.trim().toLowerCase(),
  ]);

  let deadBlinds = 0;

  if (!activePositions.has('sb')) {
    deadBlinds += 0.5;
  }

  if (!activePositions.has('bb')) {
    deadBlinds += 1;
  }

  return deadBlinds;
}

function getSpotPreflopPositions(spot: PokerStudySpotDetail['spot']) {
  const firstPosition =
    readStringFromRecord(spot.preflop_config, 'first_position') || spot.hero_position;

  const secondPosition =
    readStringFromRecord(spot.preflop_config, 'second_position') || spot.villain_position;

  return {
    firstPosition: firstPosition.toLowerCase(),
    secondPosition: secondPosition.toLowerCase(),
  };
}

function getEstimatedFlopPotBb(spot: PokerStudySpotDetail['spot']): number | null {
  const spotType = (
    readStringFromRecord(spot.preflop_config, 'spot_type') ||
    spot.spot_type ||
    ''
  ).trim().toLowerCase();

  const { firstPosition, secondPosition } = getSpotPreflopPositions(spot);

  let finalContributionBb: number | null = null;

  if (spotType === 'srp') {
    finalContributionBb = readNumberFromRecord(spot.preflop_config, 'open_raise_size_bb');
  } else if (spotType === '3bet') {
    finalContributionBb = readNumberFromRecord(spot.preflop_config, 'three_bet_size_bb');
  } else if (spotType === '4bet') {
    finalContributionBb = readNumberFromRecord(spot.preflop_config, 'four_bet_size_bb');
  } else if (spotType === 'rol') {
    finalContributionBb = readNumberFromRecord(spot.preflop_config, 'rol_size_bb');
  }

  if (finalContributionBb === null) {
    return null;
  }

  return finalContributionBb * 2 + getDeadBlindAmount(firstPosition, secondPosition);
}

function getSpotRangeSlots(spot: PokerStudySpotDetail['spot']) {
  const { firstPosition, secondPosition } = getSpotPreflopPositions(spot);

  return [
    {
      key: 'open_raise',
      title: 'Rango OR',
      position: firstPosition,
      subtitle: `${formatPosition(firstPosition)} open raise`,
    },
    {
      key: 'caller',
      title: 'Rango caller',
      position: secondPosition,
      subtitle: `${formatPosition(secondPosition)} call`,
    },
  ];
}

const SUIT_META: Record<string, { symbol: string; color: string; label: string }> = {
  s: { symbol: '♠', color: '#111827', label: 'picas' },
  h: { symbol: '♥', color: '#ef4444', label: 'corazones' },
  d: { symbol: '♦', color: '#2563eb', label: 'diamantes' },
  c: { symbol: '♣', color: '#16a34a', label: 'tréboles' },
};

function parseCardToken(value: string | null | undefined): { rank: string; suit: string } | null {
  const token = (value || '').trim();
  if (token.length !== 2) return null;

  return {
    rank: token[0].toUpperCase(),
    suit: token[1].toLowerCase(),
  };
}

function SpotPokerCard({
  value,
  compact = false,
}: {
  value: string | null | undefined;
  compact?: boolean;
}) {
  const parsed = parseCardToken(value);
  const width = compact ? 32 : 42;
  const height = compact ? 40 : 52;

  if (!parsed) {
    return (
      <Box
        sx={{
          width,
          minWidth: width,
          height,
          borderRadius: compact ? 1.2 : 1.5,
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize: compact ? 12 : 14,
        }}
      >
        ?
      </Box>
    );
  }

  const suit = SUIT_META[parsed.suit];

  return (
    <Box
      aria-label={`${parsed.rank} de ${suit?.label || parsed.suit}`}
      sx={{
        width,
        minWidth: width,
        height,
        borderRadius: compact ? 1.25 : 1.75,
        bgcolor: suit?.color || 'background.paper',
        border: '1px solid rgba(255,255,255,0.28)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
      }}
    >
      <Typography
        fontWeight={950}
        sx={{
          color: '#ffffff',
          lineHeight: 0.95,
          fontSize: compact ? 16 : 20,
        }}
      >
        {parsed.rank}
      </Typography>
      <Typography
        fontWeight={950}
        sx={{
          color: '#ffffff',
          lineHeight: 0.95,
          fontSize: compact ? 11 : 14,
        }}
      >
        {suit?.symbol || parsed.suit}
      </Typography>
    </Box>
  );
}

function buildRangeHandClass(rowRank: string, colRank: string): string {
  if (rowRank === colRank) {
    return `${rowRank}${colRank}`;
  }

  return CARD_RANKS.indexOf(rowRank) < CARD_RANKS.indexOf(colRank)
    ? `${rowRank}${colRank}s`
    : `${colRank}${rowRank}o`;
}

function buildPreflopRangeMatrix(): string[][] {
  return CARD_RANKS.map((rowRank) =>
    CARD_RANKS.map((colRank) => buildRangeHandClass(rowRank, colRank)),
  );
}

function sortHandClassesByMatrixOrder(handClasses: string[]): string[] {
  const order = new Map<string, number>();

  buildPreflopRangeMatrix().flat().forEach((handClass, index) => {
    order.set(handClass, index);
  });

  return [...handClasses].sort(
    (left, right) => (order.get(left) ?? 9999) - (order.get(right) ?? 9999),
  );
}

function getPreflopHandClassComboCount(handClass: string): number {
  if (handClass.length === 2) {
    return 6;
  }

  if (handClass.endsWith('s')) {
    return 4;
  }

  if (handClass.endsWith('o')) {
    return 12;
  }

  return 0;
}

function getCompatibleRangeTemplateFamilies(rangeKey: string): string[] {
  if (rangeKey === 'open_raise') {
    return ['open_raise'];
  }

  if (rangeKey === 'caller') {
    return ['call_vs_open', 'defend_blind'];
  }

  return [];
}

function getRangeTemplateHandClasses(template: PokerStudyPreflopRangeTemplate): string[] {
  return Array.isArray(template.range_state?.hand_classes)
    ? template.range_state.hand_classes
    : [];
}

function getPreflopRangeComboCount(handClasses: Iterable<string>): number {
  return Array.from(handClasses).reduce(
    (acc, handClass) => acc + getPreflopHandClassComboCount(handClass),
    0,
  );
}

function formatPreflopRangePercentage(comboCount: number): string {
  const totalPreflopCombos = 1326;

  if (!comboCount) {
    return '0%';
  }

  return `${((comboCount / totalPreflopCombos) * 100).toFixed(1)}%`;
}

function isPocketPairHandClass(handClass: string): boolean {
  return handClass.length === 2 && handClass[0] === handClass[1];
}

function SpotCardPickerGrid({
  selectedCards,
  onSelectCard,
}: {
  selectedCards: string[];
  onSelectCard: (card: string) => void;
}) {
  const blockedCards = new Set(selectedCards.filter(Boolean));
  const deck = CARD_SUITS.flatMap((suit) => CARD_RANKS.map((rank) => `${rank}${suit}`));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(13, 32px)',
        gap: 0.25,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {deck.map((card) => {
        const isBlocked = blockedCards.has(card);

        return (
          <Box
            key={card}
            onClick={() => {
              if (!isBlocked) {
                onSelectCard(card);
              }
            }}
            sx={{
              width: 32,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isBlocked ? 0.28 : 1,
              cursor: isBlocked ? 'not-allowed' : 'pointer',
              pointerEvents: isBlocked ? 'none' : 'auto',
              transform: 'scale(0.68)',
              transformOrigin: 'center',
            }}
          >
            <SpotPokerCard value={card} />
          </Box>
        );
      })}
    </Box>
  );
}

export default function PokerStudySpotPage() {
  const navigate = useNavigate();
  const { spotId } = useParams<{ spotId: string }>();

  const [detail, setDetail] = useState<PokerStudySpotDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isBoardDialogOpen, setIsBoardDialogOpen] = useState(false);
  const [boardDialogMode, setBoardDialogMode] = useState<BoardDialogMode>('create');
  const [editingBoard, setEditingBoard] =
    useState<PokerStudySpotDetail['boards'][number] | null>(null);
  const [boardPendingDelete, setBoardPendingDelete] =
    useState<PokerStudySpotDetail['boards'][number] | null>(null);
  const [isSubmittingBoard, setIsSubmittingBoard] = useState(false);
  const [isDeletingBoard, setIsDeletingBoard] = useState(false);
  const [boardCardPickerTarget, setBoardCardPickerTarget] =
    useState<'flop_card_1' | 'flop_card_2' | 'flop_card_3' | null>(null);
  const [selectedPreflopRangeSlot, setSelectedPreflopRangeSlot] = useState<string | null>(null);
  const [preflopRanges, setPreflopRanges] = useState<PokerStudySpotPreflopRange[]>([]);
  const [rangeTemplates, setRangeTemplates] = useState<PokerStudyPreflopRangeTemplate[]>([]);
  const [isLoadingRangeTemplates, setIsLoadingRangeTemplates] = useState(false);
  const [preflopRangeDraft, setPreflopRangeDraft] = useState<Set<string>>(new Set());
  const [isSavingPreflopRange, setIsSavingPreflopRange] = useState(false);
  const [isPreflopRangePainting, setIsPreflopRangePainting] = useState(false);
  const [preflopRangePaintMode, setPreflopRangePaintMode] =
    useState<'select' | 'clear' | null>(null);
  const [boardForm, setBoardForm] = useState<BoardFormState>(INITIAL_BOARD_FORM);

  const [spotNotesContent, setSpotNotesContent] = useState('');
  const [spotNotesAutosaveStatus, setSpotNotesAutosaveStatus] =
    useState<SpotNotesAutosaveStatus>('idle');

  const [theoryPanelMode, setTheoryPanelMode] = useState<TheoryPanelMode>('free_notes');
  const [spotStrategy, setSpotStrategy] = useState<PokerStudySpotStrategy | null>(null);
  const [activeDecisionPointId, setActiveDecisionPointId] = useState<string | null>(null);
  const [isLoadingSpotStrategy, setIsLoadingSpotStrategy] = useState(false);
  const [isCreatingFlopDecisionPoint, setIsCreatingFlopDecisionPoint] = useState(false);
  const [openingResponseActionId, setOpeningResponseActionId] = useState<string | null>(null);

  const [strategyLineDialogOpen, setStrategyLineDialogOpen] = useState(false);
  const [selectedDecisionPoint, setSelectedDecisionPoint] =
    useState<PokerStudySpotDecisionPointWorkspace | null>(null);
  const [editingStrategyLine, setEditingStrategyLine] =
    useState<PokerStudySpotStrategyLineWorkspace | null>(null);
  const [strategyLineForm, setStrategyLineForm] =
    useState<StrategyLineFormState>(INITIAL_STRATEGY_LINE_FORM);
  const [isSavingStrategyLine, setIsSavingStrategyLine] = useState(false);
  const [isDeletingStrategyLine, setIsDeletingStrategyLine] = useState(false);

  const [strategyLineActionDialogOpen, setStrategyLineActionDialogOpen] = useState(false);
  const [selectedStrategyLineForAction, setSelectedStrategyLineForAction] =
    useState<PokerStudySpotStrategyLineWorkspace | null>(null);
  const [strategyLineActionForm, setStrategyLineActionForm] =
    useState<StrategyLineActionFormState>(INITIAL_STRATEGY_LINE_ACTION_FORM);
  const [isSavingStrategyLineAction, setIsSavingStrategyLineAction] = useState(false);

  const [strategyBoardFamilyDialogOpen, setStrategyBoardFamilyDialogOpen] = useState(false);
  const [selectedStrategyLineForBoardFamily, setSelectedStrategyLineForBoardFamily] =
    useState<PokerStudySpotStrategyLineWorkspace | null>(null);
  const [strategyBoardFamilyForm, setStrategyBoardFamilyForm] =
    useState<StrategyBoardFamilyFormState>(INITIAL_STRATEGY_BOARD_FAMILY_FORM);
  const [isSavingStrategyBoardFamily, setIsSavingStrategyBoardFamily] = useState(false);

  const spotNotesEditorRef = useRef<HTMLDivElement | null>(null);
  const spotNotesAutosaveTimerRef = useRef<number | null>(null);
  const spotNotesLastSavedValueRef = useRef('');

  const [boardHighCardFilter, setBoardHighCardFilter] = useState('all');
  const [boardPairedFilter, setBoardPairedFilter] = useState('all');
  const [boardBroadwayFilter, setBoardBroadwayFilter] = useState('all');
  const [boardSuitedFilter, setBoardSuitedFilter] = useState('all');
  const [boardConnectivityFilter, setBoardConnectivityFilter] = useState('all');
  const [boardStraightFilter, setBoardStraightFilter] = useState('all');
  const [activeBoardTypeLabel, setActiveBoardTypeLabel] = useState<string | null>(null);
  const [boardFilterPresetDialogOpen, setBoardFilterPresetDialogOpen] = useState(false);
  const [boardTypeDialogMode, setBoardTypeDialogMode] =
    useState<BoardTypeDialogMode>('create');
  const [selectedBoardTypeLine, setSelectedBoardTypeLine] =
    useState<PokerStudySpotStrategyLineWorkspace | null>(null);
  const [editingBoardTypePreset, setEditingBoardTypePreset] =
    useState<PokerStudyBoardFilterPreset | null>(null);
  const [boardFilterPresetForm, setBoardFilterPresetForm] =
    useState<BoardFilterPresetFormState>(INITIAL_BOARD_FILTER_PRESET_FORM);
  const [isSavingBoardFilterPreset, setIsSavingBoardFilterPreset] = useState(false);

  async function loadRangeTemplates() {
    setIsLoadingRangeTemplates(true);

    try {
      const rows = await listPokerStudyPreflopRangeTemplates();
      setRangeTemplates(rows);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudieron cargar las plantillas de rango.');
    } finally {
      setIsLoadingRangeTemplates(false);
    }
  }


  async function loadDetail() {
    if (!spotId) {
      setErrorMessage('No se ha podido resolver el spot.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const [response, ranges] = await Promise.all([
        getPokerStudySpotDetail(spotId),
        listPokerStudySpotPreflopRanges(spotId),
      ]);

      setDetail(response);
      setPreflopRanges(ranges);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo cargar el detalle del spot.');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadSpotStrategy() {
    if (!spotId) {
      setSpotStrategy(null);
      return;
    }

    setIsLoadingSpotStrategy(true);

    try {
      const strategy = await getPokerStudySpotStrategy(spotId);
      setSpotStrategy(strategy);

      if (!activeDecisionPointId) {
        const rootPoint =
          strategy.decision_points.find((point) => point.decision_key === 'flop_root') ||
          strategy.decision_points[0] ||
          null;

        setActiveDecisionPointId(rootPoint?.id ?? null);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudieron cargar las líneas estratégicas del spot.');
    } finally {
      setIsLoadingSpotStrategy(false);
    }
  }

  async function handleChangeTheoryPanelMode(nextMode: TheoryPanelMode) {
    if (nextMode === theoryPanelMode) return;

    if (theoryPanelMode === 'free_notes') {
      syncSpotNotesContentFromEditor();
      await persistCurrentSpotNotes();
    }

    setTheoryPanelMode(nextMode);
  }

  async function handleCreateFlopDecisionPoint() {
    if (!spotId) return;

    setIsCreatingFlopDecisionPoint(true);
    setErrorMessage(null);

    try {
      await createPokerStudySpotFlopDecisionPoint(spotId);
      await loadSpotStrategy();
      setTheoryPanelMode('strategy_map');
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo crear el primer punto de decisión del spot.');
    } finally {
      setIsCreatingFlopDecisionPoint(false);
    }
  }

  function openCreateStrategyLineDialog(decisionPoint: PokerStudySpotDecisionPointWorkspace) {
    setSelectedDecisionPoint(decisionPoint);
    setEditingStrategyLine(null);
    setStrategyLineForm(INITIAL_STRATEGY_LINE_FORM);
    setErrorMessage(null);
    setStrategyLineDialogOpen(true);
  }

  function openEditStrategyLineDialog(line: PokerStudySpotStrategyLineWorkspace) {
    setSelectedDecisionPoint(null);
    setEditingStrategyLine(line);
    setStrategyLineForm({
      name: line.name,
      strategy_type: line.strategy_type || 'general',
      summary: line.summary || '',
      notes: line.notes || '',
    });
    setErrorMessage(null);
    setStrategyLineDialogOpen(true);
  }

  function closeStrategyLineDialog() {
    if (isSavingStrategyLine) return;

    setStrategyLineDialogOpen(false);
    setSelectedDecisionPoint(null);
    setEditingStrategyLine(null);
    setStrategyLineForm(INITIAL_STRATEGY_LINE_FORM);
  }

  async function handleSubmitStrategyLine() {
    const name = strategyLineForm.name.trim();

    if (!name) {
      setErrorMessage('Introduce un nombre para la línea estratégica.');
      return;
    }

    setIsSavingStrategyLine(true);
    setErrorMessage(null);

    try {
      if (editingStrategyLine) {
        await updatePokerStudySpotStrategyLine(editingStrategyLine.id, {
          name,
          strategy_type: strategyLineForm.strategy_type.trim() || 'general',
          summary: strategyLineForm.summary.trim(),
          color_token: editingStrategyLine.color_token,
          notes: strategyLineForm.notes.trim(),
          display_order: editingStrategyLine.display_order,
          metadata: editingStrategyLine.metadata || {},
        });

        closeStrategyLineDialog();
        await loadSpotStrategy();
        return;
      }

      if (!selectedDecisionPoint) {
        setErrorMessage('No se ha podido resolver el punto de decisión.');
        return;
      }

      await createPokerStudySpotStrategyLine(selectedDecisionPoint.id, {
        name,
        strategy_type: strategyLineForm.strategy_type.trim() || 'general',
        summary: strategyLineForm.summary.trim(),
        color_token: null,
        notes: strategyLineForm.notes.trim(),
        display_order: selectedDecisionPoint.strategy_lines.length,
        metadata: {
          source: 'spot_strategy_lines_v1',
        },
      });

      closeStrategyLineDialog();
      await loadSpotStrategy();
    } catch (error) {
      console.error(error);
      setErrorMessage(
        editingStrategyLine
          ? 'No se pudo actualizar la línea estratégica.'
          : 'No se pudo crear la línea estratégica.',
      );
    } finally {
      setIsSavingStrategyLine(false);
    }
  }

  async function handleDeleteStrategyLine(line: PokerStudySpotStrategyLineWorkspace) {
    const confirmed = window.confirm(
      `¿Eliminar la línea "${line.name}"? También se eliminarán sus acciones y tipos de board asociados.`,
    );

    if (!confirmed) return;

    setIsDeletingStrategyLine(true);
    setErrorMessage(null);

    try {
      await deletePokerStudySpotStrategyLine(line.id);
      await loadSpotStrategy();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo eliminar la línea estratégica.');
    } finally {
      setIsDeletingStrategyLine(false);
    }
  }


  async function handleOpenResponseDecisionPoint(actionId: string) {
    setOpeningResponseActionId(actionId);
    setErrorMessage(null);

    try {
      const responsePoint = await createPokerStudyResponseDecisionPointFromAction(actionId);
      await loadSpotStrategy();
      setActiveDecisionPointId(responsePoint.id);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo abrir el punto de respuesta.');
    } finally {
      setOpeningResponseActionId(null);
    }
  }

  function openStrategyLineActionDialog(line: PokerStudySpotStrategyLineWorkspace) {
    setSelectedStrategyLineForAction(line);
    setStrategyLineActionForm(INITIAL_STRATEGY_LINE_ACTION_FORM);
    setErrorMessage(null);
    setStrategyLineActionDialogOpen(true);
  }

  function closeStrategyLineActionDialog() {
    if (isSavingStrategyLineAction) return;

    setStrategyLineActionDialogOpen(false);
    setSelectedStrategyLineForAction(null);
    setStrategyLineActionForm(INITIAL_STRATEGY_LINE_ACTION_FORM);
  }

  async function handleSubmitStrategyLineAction() {
    if (!selectedStrategyLineForAction) return;

    const actionType = strategyLineActionForm.action_type;
    const needsSize = actionType === 'bet' || actionType === 'raise';
    let parsedSize: number | null = null;

    if (needsSize) {
      parsedSize = Number(strategyLineActionForm.size_percentage.replace(',', '.'));

      if (!Number.isFinite(parsedSize) || parsedSize <= 0) {
        setErrorMessage('Introduce un tamaño válido para la acción.');
        return;
      }
    }

    const label =
      strategyLineActionForm.label.trim() ||
      formatTheoryAssociatedActionLabel(actionType, parsedSize);

    setIsSavingStrategyLineAction(true);
    setErrorMessage(null);

    try {
      await createPokerStudySpotStrategyLineAction(selectedStrategyLineForAction.id, {
        action_type: actionType,
        size_percentage: parsedSize,
        label,
        weight_label: strategyLineActionForm.weight_label.trim() || null,
        notes: strategyLineActionForm.notes.trim(),
        display_order: selectedStrategyLineForAction.actions.length,
        metadata: {
          source: 'spot_strategy_lines_v1',
        },
      });

      closeStrategyLineActionDialog();
      await loadSpotStrategy();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo crear la acción de la línea.');
    } finally {
      setIsSavingStrategyLineAction(false);
    }
  }

  function closeStrategyBoardFamilyDialog() {
    if (isSavingStrategyBoardFamily) return;

    setStrategyBoardFamilyDialogOpen(false);
    setSelectedStrategyLineForBoardFamily(null);
    setStrategyBoardFamilyForm(INITIAL_STRATEGY_BOARD_FAMILY_FORM);
  }

  async function handleSubmitStrategyBoardFamily() {
    if (!selectedStrategyLineForBoardFamily) return;

    const title = strategyBoardFamilyForm.title.trim();

    if (!title) {
      setErrorMessage('Introduce un nombre para el tipo de board.');
      return;
    }

    const tags = strategyBoardFamilyForm.tags_text
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    setIsSavingStrategyBoardFamily(true);
    setErrorMessage(null);

    try {
      await createPokerStudySpotStrategyBoardFamily(selectedStrategyLineForBoardFamily.id, {
        title,
        description: strategyBoardFamilyForm.description.trim(),
        examples_text: strategyBoardFamilyForm.examples_text.trim(),
        tags,
        pattern_config: {},
        notes: strategyBoardFamilyForm.notes.trim(),
        display_order: selectedStrategyLineForBoardFamily.board_families.length,
        metadata: {
          source: 'spot_strategy_lines_v1',
        },
      });

      closeStrategyBoardFamilyDialog();
      await loadSpotStrategy();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo crear el tipo de board.');
    } finally {
      setIsSavingStrategyBoardFamily(false);
    }
  }

  useEffect(() => {
    void loadDetail();
    void loadSpotStrategy();
  }, [spotId]);

  useEffect(() => {
    if (theoryPanelMode !== 'free_notes') return;
    if (!spotNotesEditorRef.current) return;

    if (spotNotesEditorRef.current.innerHTML !== spotNotesContent) {
      spotNotesEditorRef.current.innerHTML = spotNotesContent || '';
    }
  }, [theoryPanelMode, spotNotesContent]);

  useEffect(() => {
    function stopPreflopRangePainting() {
      setIsPreflopRangePainting(false);
      setPreflopRangePaintMode(null);
    }

    window.addEventListener('mouseup', stopPreflopRangePainting);

    return () => {
      window.removeEventListener('mouseup', stopPreflopRangePainting);
    };
  }, []);

  useEffect(() => {
    const nextContent = detail?.spot_notes?.content || '';

    setSpotNotesContent(nextContent);
    spotNotesLastSavedValueRef.current = nextContent;
    setSpotNotesAutosaveStatus('idle');

    if (spotNotesEditorRef.current) {
      spotNotesEditorRef.current.innerHTML = nextContent;
    }
  }, [detail?.spot_notes?.content]);

  const persistCurrentSpotNotes = useCallback(
    async (content = spotNotesContent): Promise<boolean> => {
      if (!spotId) return true;

      if (content === spotNotesLastSavedValueRef.current) {
        return true;
      }

      setSpotNotesAutosaveStatus('saving');

      try {
        const saved = await upsertPokerStudySpotNotes(spotId, { content });
        spotNotesLastSavedValueRef.current = saved.content;
        setSpotNotesAutosaveStatus('saved');
        return true;
      } catch (error) {
        console.error(error);
        setSpotNotesAutosaveStatus('error');
        setErrorMessage('No se pudieron guardar los apuntes del spot.');
        return false;
      }
    },
    [spotId, spotNotesContent],
  );

  useEffect(() => {
    if (!spotId) return;

    if (spotNotesContent === spotNotesLastSavedValueRef.current) {
      return;
    }

    setSpotNotesAutosaveStatus('saving');

    if (spotNotesAutosaveTimerRef.current) {
      window.clearTimeout(spotNotesAutosaveTimerRef.current);
    }

    spotNotesAutosaveTimerRef.current = window.setTimeout(() => {
      void persistCurrentSpotNotes(spotNotesContent);
      spotNotesAutosaveTimerRef.current = null;
    }, 850);

    return () => {
      if (spotNotesAutosaveTimerRef.current) {
        window.clearTimeout(spotNotesAutosaveTimerRef.current);
        spotNotesAutosaveTimerRef.current = null;
      }
    };
  }, [persistCurrentSpotNotes, spotId, spotNotesContent]);

  function syncSpotNotesContentFromEditor() {
    const nextContent = spotNotesEditorRef.current?.innerHTML || '';
    setSpotNotesContent(nextContent);
  }

  function applySpotNotesCommand(command: 'bold' | 'italic' | 'insertUnorderedList') {
    spotNotesEditorRef.current?.focus();
    document.execCommand(command, false);
    syncSpotNotesContentFromEditor();
  }

  function insertSuitInSpotNotes(symbol: string, color: string) {
    spotNotesEditorRef.current?.focus();

    document.execCommand(
      'insertHTML',
      false,
      `<span style="color:${color};font-weight:900">${symbol}</span>&nbsp;`,
    );

    syncSpotNotesContentFromEditor();
  }

  function openBoardDialog() {
    setBoardDialogMode('create');
    setEditingBoard(null);
    setBoardForm(INITIAL_BOARD_FORM);
    setErrorMessage(null);
    setIsBoardDialogOpen(true);
  }

  function openEditBoardDialog(board: PokerStudySpotDetail['boards'][number]) {
    setBoardDialogMode('edit');
    setEditingBoard(board);
    setBoardForm(buildBoardFormFromBoard(board));
    setErrorMessage(null);
    setIsBoardDialogOpen(true);
  }

  function closeBoardDialog() {
    if (isSubmittingBoard) return;
    setIsBoardDialogOpen(false);
    setEditingBoard(null);
    setBoardDialogMode('create');
    setBoardCardPickerTarget(null);
  }

  async function handleSubmitBoard() {
    if (!spotId || !detail) return;

    if (boardForm.board_mode === 'exact_board') {
      const selectedCards = [
        boardForm.flop_card_1.trim(),
        boardForm.flop_card_2.trim(),
        boardForm.flop_card_3.trim(),
      ];

      if (selectedCards.some((card) => !card)) {
        setErrorMessage('Selecciona las tres cartas del flop.');
        return;
      }

      if (new Set(selectedCards).size !== selectedCards.length) {
        setErrorMessage('No puede haber cartas duplicadas en el board.');
        return;
      }
    } else if (!boardForm.board_family_label.trim()) {
      setErrorMessage('Para board family debes informar una etiqueta.');
      return;
    }

    setIsSubmittingBoard(true);
    setErrorMessage(null);

    try {
      const payload = buildBoardPayload(boardForm, detail.boards.length);

      if (boardDialogMode === 'edit' && editingBoard) {
        await updatePokerStudyBoard(editingBoard.id, {
          ...payload,
          display_order: editingBoard.display_order,
        });
        setIsBoardDialogOpen(false);
        setEditingBoard(null);
        setBoardDialogMode('create');
        await loadDetail();
        return;
      }

      const createdBoard = await createPokerStudyBoard(spotId, payload);
      setIsBoardDialogOpen(false);
      await loadDetail();
      navigate(`/poker-study/boards/${createdBoard.id}`);
    } catch (error) {
      console.error(error);
      setErrorMessage(
        boardDialogMode === 'edit'
          ? 'No se pudo actualizar el board.'
          : 'No se pudo crear el board.',
      );
    } finally {
      setIsSubmittingBoard(false);
    }
  }

  async function handleDeleteBoard() {
    if (!boardPendingDelete) return;

    const boardIdToDelete = boardPendingDelete.id;

    setIsDeletingBoard(true);
    setErrorMessage(null);

    try {
      if (theoryPanelMode === 'free_notes') {
        const currentNotesContent = spotNotesEditorRef.current?.innerHTML || spotNotesContent;
        setSpotNotesContent(currentNotesContent);

        const notesSaved = await persistCurrentSpotNotes(currentNotesContent);
        if (!notesSaved) {
          return;
        }
      }

      await deletePokerStudyBoard(boardIdToDelete);

      setDetail((current) =>
        current
          ? {
              ...current,
              boards: current.boards.filter((board) => board.id !== boardIdToDelete),
            }
          : current,
      );

      setBoardPendingDelete(null);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo eliminar el board.');
    } finally {
      setIsDeletingBoard(false);
    }
  }

  const totalBoards = useMemo(
    () => detail?.boards.length || 0,
    [detail],
  );

  const boardHighCardOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (detail?.boards || [])
            .map((board) => board.high_card_rank)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort(),
    [detail],
  );

  const boardPairedOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (detail?.boards || [])
            .map((board) => board.paired_status)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort(),
    [detail],
  );

  const boardSuitedOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (detail?.boards || [])
            .map((board) => board.suited_texture)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort(),
    [detail],
  );

  const filteredBoards = useMemo(
    () =>
      (detail?.boards || []).filter((board) => {
        const broadwayCount = getBoardBroadwayCount(board);

        const matchesHighCard =
          boardHighCardFilter === 'all' || board.high_card_rank === boardHighCardFilter;

        const matchesPaired =
          boardPairedFilter === 'all' || board.paired_status === boardPairedFilter;

        const matchesBroadways =
          boardBroadwayFilter === 'all' || broadwayCount === Number(boardBroadwayFilter);

        const matchesSuited =
          boardSuitedFilter === 'all' || board.suited_texture === boardSuitedFilter;

        const matchesConnectivity =
          boardConnectivityFilter === 'all' ||
          board.connectivity_texture === boardConnectivityFilter;

        const matchesStraight =
          boardStraightFilter === 'all' ||
          (boardStraightFilter === 'yes' && board.straight_possible) ||
          (boardStraightFilter === 'no' && !board.straight_possible);

        return (
          matchesHighCard &&
          matchesPaired &&
          matchesBroadways &&
          matchesSuited &&
          matchesConnectivity &&
          matchesStraight
        );
      }),
    [
      boardBroadwayFilter,
      boardConnectivityFilter,
      boardHighCardFilter,
      boardPairedFilter,
      boardStraightFilter,
      boardSuitedFilter,
      detail,
    ],
  );

  const hasActiveBoardFilters =
    boardHighCardFilter !== 'all' ||
    boardPairedFilter !== 'all' ||
    boardBroadwayFilter !== 'all' ||
    boardSuitedFilter !== 'all' ||
    boardConnectivityFilter !== 'all' ||
    boardStraightFilter !== 'all';

  function applyBoardFilterConfig(
    filters: PokerStudyBoardFilterPresetConfig,
    label: string | null,
  ) {
    setBoardHighCardFilter(filters.high_card_rank || 'all');
    setBoardPairedFilter(filters.paired_status || 'all');
    setBoardBroadwayFilter(filters.broadway_count || 'all');
    setBoardSuitedFilter(filters.suited_texture || 'all');
    setBoardConnectivityFilter(filters.connectivity_texture || 'all');
    setBoardStraightFilter(filters.straight_possible || 'all');
    setActiveBoardTypeLabel(label);
  }

  function clearBoardFilters() {
    setBoardHighCardFilter('all');
    setBoardPairedFilter('all');
    setBoardBroadwayFilter('all');
    setBoardSuitedFilter('all');
    setBoardConnectivityFilter('all');
    setBoardStraightFilter('all');
    setActiveBoardTypeLabel(null);
  }

  function handleApplyBoardTypePreset(
    line: PokerStudySpotStrategyLineWorkspace,
    preset: PokerStudyBoardFilterPreset,
  ) {
    applyBoardFilterConfig(preset.filters, `${line.name}: ${preset.label}`);
  }

  function openBoardTypeDialog(line: PokerStudySpotStrategyLineWorkspace) {
    setBoardTypeDialogMode('create');
    setSelectedBoardTypeLine(line);
    setEditingBoardTypePreset(null);
    setBoardFilterPresetForm(INITIAL_BOARD_FILTER_PRESET_FORM);
    setErrorMessage(null);
    setBoardFilterPresetDialogOpen(true);
  }

  function openEditBoardTypeDialog(
    line: PokerStudySpotStrategyLineWorkspace,
    preset: PokerStudyBoardFilterPreset,
  ) {
    setBoardTypeDialogMode('edit');
    setSelectedBoardTypeLine(line);
    setEditingBoardTypePreset(preset);
    setBoardFilterPresetForm(buildBoardFilterPresetFormFromPreset(preset));
    setErrorMessage(null);
    setBoardFilterPresetDialogOpen(true);
  }

  function closeBoardFilterPresetDialog() {
    if (isSavingBoardFilterPreset) return;

    setBoardFilterPresetDialogOpen(false);
    setBoardTypeDialogMode('create');
    setSelectedBoardTypeLine(null);
    setEditingBoardTypePreset(null);
    setBoardFilterPresetForm(INITIAL_BOARD_FILTER_PRESET_FORM);
  }

  async function handleDeleteBoardTypePreset(
    line: PokerStudySpotStrategyLineWorkspace,
    preset: PokerStudyBoardFilterPreset,
  ) {
    const confirmed = window.confirm(`¿Eliminar el tipo de board "${preset.label}"?`);

    if (!confirmed) return;

    const existingPresets = readBoardTypePresetsFromLine(line);
    const nextPresets = existingPresets.filter((item) => item.id !== preset.id);

    setIsSavingBoardFilterPreset(true);
    setErrorMessage(null);

    try {
      await updatePokerStudySpotStrategyLine(line.id, {
        name: line.name,
        strategy_type: line.strategy_type || 'general',
        summary: line.summary || '',
        color_token: line.color_token,
        notes: line.notes || '',
        display_order: line.display_order,
        metadata: {
          ...(line.metadata || {}),
          board_type_presets: nextPresets,
        },
      });

      if (activeBoardTypeLabel === `${line.name}: ${preset.label}`) {
        clearBoardFilters();
      }

      await loadSpotStrategy();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo eliminar el tipo de board.');
    } finally {
      setIsSavingBoardFilterPreset(false);
    }
  }

  function buildBoardFilterPresetConfigFromForm(): PokerStudyBoardFilterPresetConfig {
    return {
      high_card_rank:
        boardFilterPresetForm.high_card_rank === 'all'
          ? undefined
          : boardFilterPresetForm.high_card_rank,
      paired_status:
        boardFilterPresetForm.paired_status === 'all'
          ? undefined
          : boardFilterPresetForm.paired_status,
      broadway_count:
        boardFilterPresetForm.broadway_count === 'all'
          ? undefined
          : boardFilterPresetForm.broadway_count,
      suited_texture:
        boardFilterPresetForm.suited_texture === 'all'
          ? undefined
          : boardFilterPresetForm.suited_texture,
      connectivity_texture:
        boardFilterPresetForm.connectivity_texture === 'all'
          ? undefined
          : boardFilterPresetForm.connectivity_texture,
      straight_possible:
        boardFilterPresetForm.straight_possible === 'all'
          ? undefined
          : boardFilterPresetForm.straight_possible,
    };
  }

  async function handleSubmitBoardFilterPreset() {
    if (!selectedBoardTypeLine) return;

    const filters = buildBoardFilterPresetConfigFromForm();
    const label =
      boardFilterPresetForm.label.trim() || buildBoardTypeTitleFromForm(boardFilterPresetForm);

    const existingPresets = readBoardTypePresetsFromLine(selectedBoardTypeLine);
    const nextPreset: PokerStudyBoardFilterPreset = {
      id:
        boardTypeDialogMode === 'edit' && editingBoardTypePreset
          ? editingBoardTypePreset.id
          : `preset_${Date.now()}`,
      label,
      filters,
    };

    const nextPresets =
      boardTypeDialogMode === 'edit' && editingBoardTypePreset
        ? existingPresets.map((preset) =>
            preset.id === editingBoardTypePreset.id ? nextPreset : preset,
          )
        : [...existingPresets, nextPreset];

    setIsSavingBoardFilterPreset(true);
    setErrorMessage(null);

    try {
      await updatePokerStudySpotStrategyLine(selectedBoardTypeLine.id, {
        name: selectedBoardTypeLine.name,
        strategy_type: selectedBoardTypeLine.strategy_type || 'general',
        summary: selectedBoardTypeLine.summary || '',
        color_token: selectedBoardTypeLine.color_token,
        notes: selectedBoardTypeLine.notes || '',
        display_order: selectedBoardTypeLine.display_order,
        metadata: {
          ...(selectedBoardTypeLine.metadata || {}),
          board_type_presets: nextPresets,
        },
      });

      closeBoardFilterPresetDialog();
      await loadSpotStrategy();

      applyBoardFilterConfig(
        nextPreset.filters,
        `${selectedBoardTypeLine.name}: ${nextPreset.label}`,
      );
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo guardar el tipo de board.');
    } finally {
      setIsSavingBoardFilterPreset(false);
    }
  }

  const estimatedFlopPotBb = detail ? getEstimatedFlopPotBb(detail.spot) : null;

  const primaryDecisionPoint = useMemo(() => {
    if (!spotStrategy?.decision_points.length) return null;

    return (
      spotStrategy.decision_points.find((point) => point.id === activeDecisionPointId) ||
      spotStrategy.decision_points.find((point) => point.decision_key === 'flop_root') ||
      spotStrategy.decision_points[0]
    );
  }, [activeDecisionPointId, spotStrategy]);

  const parentDecisionPoint = useMemo(() => {
    if (!spotStrategy || !primaryDecisionPoint?.parent_decision_point_id) return null;

    return (
      spotStrategy.decision_points.find(
        (point) => point.id === primaryDecisionPoint.parent_decision_point_id,
      ) || null
    );
  }, [primaryDecisionPoint, spotStrategy]);

  const spotRangeSlots = useMemo(
    () => (detail ? getSpotRangeSlots(detail.spot) : []),
    [detail],
  );

  const selectedRangeSlot = useMemo(
    () => spotRangeSlots.find((slot) => slot.key === selectedPreflopRangeSlot) ?? null,
    [selectedPreflopRangeSlot, spotRangeSlots],
  );

  const preflopRangeByKey = useMemo(
    () =>
      Object.fromEntries(
        preflopRanges.map((range) => [range.range_key, range]),
      ) as Record<string, PokerStudySpotPreflopRange | undefined>,
    [preflopRanges],
  );

  const compatibleRangeTemplates = useMemo(() => {
    if (!selectedRangeSlot) {
      return [];
    }

    const allowedFamilies = getCompatibleRangeTemplateFamilies(selectedRangeSlot.key);
    const selectedPosition = selectedRangeSlot.position.trim().toLowerCase();

    return rangeTemplates.filter((template) => {
      const matchesPosition = template.position.trim().toLowerCase() === selectedPosition;
      const matchesFamily =
        allowedFamilies.length === 0 ||
        allowedFamilies.includes(template.range_family.trim().toLowerCase());

      return matchesPosition && matchesFamily;
    });
  }, [rangeTemplates, selectedRangeSlot]);

  function openPreflopRangeDialog(rangeKey: string) {
    const existingRange = preflopRangeByKey[rangeKey];
    const existingHandClasses = existingRange?.range_state?.hand_classes || [];

    setPreflopRangeDraft(new Set(existingHandClasses));
    setSelectedPreflopRangeSlot(rangeKey);

    if (rangeTemplates.length === 0) {
      void loadRangeTemplates();
    }
  }

  function closePreflopRangeDialog() {
    if (isSavingPreflopRange) return;

    setSelectedPreflopRangeSlot(null);
    setPreflopRangeDraft(new Set());
  }

  function importTemplateIntoPreflopDraft(template: PokerStudyPreflopRangeTemplate) {
    setPreflopRangeDraft(new Set(getRangeTemplateHandClasses(template)));
  }


  function applyPreflopRangePaint(handClass: string, mode: 'select' | 'clear') {
    setPreflopRangeDraft((current) => {
      const next = new Set(current);

      if (mode === 'select') {
        next.add(handClass);
      } else {
        next.delete(handClass);
      }

      return next;
    });
  }

  function startPreflopRangePaint(handClass: string) {
    const mode = preflopRangeDraft.has(handClass) ? 'clear' : 'select';

    setIsPreflopRangePainting(true);
    setPreflopRangePaintMode(mode);
    applyPreflopRangePaint(handClass, mode);
  }

  function continuePreflopRangePaint(handClass: string) {
    if (!isPreflopRangePainting || !preflopRangePaintMode) {
      return;
    }

    applyPreflopRangePaint(handClass, preflopRangePaintMode);
  }

  async function handleSavePreflopRange() {
    if (!spotId || !selectedRangeSlot) return;

    setIsSavingPreflopRange(true);
    setErrorMessage(null);

    try {
      const saved = await upsertPokerStudySpotPreflopRange(
        spotId,
        selectedRangeSlot.key,
        {
          position: selectedRangeSlot.position,
          label: selectedRangeSlot.title,
          range_state: {
            version: 1,
            hand_classes: sortHandClassesByMatrixOrder(Array.from(preflopRangeDraft)),
          },
        },
      );

      setPreflopRanges((current) => {
        const others = current.filter((range) => range.range_key !== saved.range_key);
        return [...others, saved];
      });

      closePreflopRangeDialog();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo guardar el rango preflop.');
    } finally {
      setIsSavingPreflopRange(false);
    }
  }

  return (
    <Box
      sx={{
        p: 2.5,
        minHeight: { xs: 'auto', lg: 'calc(100vh - 88px)' },
        overflow: 'visible',
        boxSizing: 'border-box',
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : !detail ? (
        <Alert severity="warning">No se ha encontrado información del spot.</Alert>
      ) : (
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              bgcolor: 'rgba(15, 23, 42, 0.22)',
              borderColor: 'rgba(148, 163, 184, 0.18)',
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
            }}
          >
            <CardContent sx={{ px: 2.1, py: 1.45, '&:last-child': { pb: 1.45 } }}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', lg: 'center' }}
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  spacing={1.35}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/poker-study')}
                    sx={{
                      minHeight: 46,
                      borderRadius: 2.5,
                      px: 1.35,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.85,
                      color: '#e2e8f0',
                      borderColor: 'rgba(148, 163, 184, 0.22)',
                      bgcolor: 'rgba(255,255,255,0.035)',
                      textTransform: 'none',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
                      '&:hover': {
                        bgcolor: 'rgba(76, 201, 216, 0.12)',
                        borderColor: 'rgba(76, 201, 216, 0.45)',
                        color: '#ffffff',
                        transform: 'translateY(-1px)',
                      },
                    }}
                    aria-label="Volver a la biblioteca de spots"
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 20,
                        fontWeight: 950,
                        lineHeight: 1,
                      }}
                    >
                      ←
                    </Typography>

                    <Box
                      component="span"
                      sx={{
                        display: { xs: 'none', sm: 'block' },
                        textAlign: 'left',
                        lineHeight: 1,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          display: 'block',
                          fontSize: 10,
                          fontWeight: 950,
                          letterSpacing: 0.7,
                          color: '#64748b',
                          lineHeight: 1,
                          textTransform: 'uppercase',
                        }}
                      >
                        Volver
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          display: 'block',
                          mt: 0.35,
                          fontSize: 12.5,
                          fontWeight: 950,
                          color: '#f8fafc',
                          lineHeight: 1,
                        }}
                      >
                        Spots
                      </Typography>
                    </Box>
                  </Button>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={950}>
                      SPOT WORKSPACE
                    </Typography>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 0.55, sm: 1 }}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ mt: 0.15 }}
                    >
                      <Typography variant="h5" fontWeight={950} sx={{ lineHeight: 1.1 }}>
                        {detail.spot.title}
                      </Typography>

                      <Chip
                        size="small"
                        label={`Bote flop: ${formatBbAmount(estimatedFlopPotBb)}`}
                        sx={{
                          height: 23,
                          fontWeight: 900,
                          bgcolor: 'rgba(76, 201, 216, 0.12)',
                          color: '#a5f3fc',
                          border: '1px solid rgba(76, 201, 216, 0.28)',
                        }}
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      alignItems="center"
                      sx={{ mt: 0.75 }}
                    >
                      <Chip
                        size="small"
                        label={`Rival · ${formatSpotVillainProfileLabel(detail.spot.villain_profile)}`}
                        sx={{
                          height: 24,
                          fontWeight: 900,
                          color: '#c4b5fd',
                          bgcolor: 'rgba(139, 92, 246, 0.12)',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                        }}
                      />

                      {detail.spot.effective_stack_bb !== null &&
                      detail.spot.effective_stack_bb !== undefined ? (
                        <Chip
                          size="small"
                          label={`Stack efectivo · ${detail.spot.effective_stack_bb}bb`}
                          sx={{
                            height: 24,
                            fontWeight: 900,
                            color: '#86efac',
                            bgcolor: 'rgba(34, 197, 94, 0.12)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                          }}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.85}
                  alignItems="center"
                  justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                  flexWrap="wrap"
                  sx={{ flexShrink: 0 }}
                >
                  {spotRangeSlots.map((slot) => {
                    const savedRange = preflopRangeByKey[slot.key];
                    const savedHandClasses = savedRange?.range_state?.hand_classes || [];
                    const comboCount = getPreflopRangeComboCount(savedHandClasses);
                    const rangePercentage = formatPreflopRangePercentage(comboCount);

                    return (
                      <Button
                        key={slot.key}
                        variant="outlined"
                        size="small"
                        onClick={() => openPreflopRangeDialog(slot.key)}
                        sx={{
                          minHeight: 34,
                          borderRadius: 2,
                          px: 1.35,
                          fontWeight: 900,
                          borderColor: comboCount > 0
                            ? 'rgba(34, 197, 94, 0.5)'
                            : 'rgba(76, 201, 216, 0.42)',
                          color: comboCount > 0 ? '#86efac' : 'primary.main',
                          bgcolor: comboCount > 0
                            ? 'rgba(34, 197, 94, 0.1)'
                            : 'rgba(255,255,255,0.025)',
                          '&:hover': {
                            bgcolor: comboCount > 0
                              ? 'rgba(34, 197, 94, 0.14)'
                              : 'rgba(76, 201, 216, 0.12)',
                            borderColor: comboCount > 0 ? '#86efac' : 'primary.main',
                          },
                        }}
                      >
                        <Stack spacing={0} alignItems="flex-start" sx={{ lineHeight: 1 }}>
                          <Typography component="span" fontSize={11} fontWeight={950}>
                            {slot.title}
                          </Typography>
                          <Typography component="span" fontSize={10.5} color="text.secondary">
                            {formatPosition(slot.position)} ·{' '}
                            {comboCount > 0
                              ? `${comboCount} combos · ${rangePercentage}`
                              : 'Sin rango'}
                          </Typography>
                        </Stack>
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Grid
            container
            spacing={2.25}
            alignItems="stretch"
            sx={{ flex: 1, minHeight: 0 }}
          >
            <Grid item xs={12} lg={9} sx={{ minHeight: 0 }}>
              <Card
                variant="outlined"
                sx={{
                  height: { xs: 'auto', lg: 'clamp(640px, calc(100vh - 245px), 760px)' },
                  maxHeight: { xs: 'none', lg: 'clamp(640px, calc(100vh - 245px), 760px)' },
                  minHeight: { xs: 520, lg: 0 },
                  borderRadius: 3,
                  bgcolor: 'rgba(15, 23, 42, 0.26)',
                  borderColor: 'rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.14)',
                  overflow: 'hidden',
                }}
              >
                <CardContent
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '&:last-child': { pb: 2 },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ mb: 1.15 }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={950}>
                        TEORÍA DEL SPOT
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Apuntes libres y líneas estratégicas del spot según familias de board.
                      </Typography>
                    </Box>

                    {theoryPanelMode === 'free_notes' ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          spotNotesAutosaveStatus === 'saving'
                            ? 'Guardando...'
                            : spotNotesAutosaveStatus === 'saved'
                              ? 'Guardado'
                              : spotNotesAutosaveStatus === 'error'
                                ? 'Error'
                                : 'Autosave'
                        }
                        color={spotNotesAutosaveStatus === 'error' ? 'error' : 'default'}
                        sx={{ fontWeight: 850, flexShrink: 0 }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          isLoadingSpotStrategy
                            ? 'Cargando líneas...'
                            : primaryDecisionPoint
                              ? `${primaryDecisionPoint.strategy_lines.length} líneas`
                              : 'Sin líneas'
                        }
                        color={primaryDecisionPoint ? 'primary' : 'default'}
                        sx={{ fontWeight: 850, flexShrink: 0 }}
                      />
                    )}
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Button
                      size="small"
                      variant={theoryPanelMode === 'free_notes' ? 'contained' : 'outlined'}
                      onClick={() => void handleChangeTheoryPanelMode('free_notes')}
                      sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                      Apuntes libres
                    </Button>

                    <Button
                      size="small"
                      variant={theoryPanelMode === 'strategy_map' ? 'contained' : 'outlined'}
                      onClick={() => void handleChangeTheoryPanelMode('strategy_map')}
                      sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                      Líneas del spot
                    </Button>
                  </Stack>

                  {theoryPanelMode === 'free_notes' ? (
                    <>
                      <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => applySpotNotesCommand('bold')}
                          sx={{ minWidth: 32, px: 0.75, fontWeight: 950 }}
                        >
                          B
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => applySpotNotesCommand('italic')}
                          sx={{ minWidth: 32, px: 0.75, fontStyle: 'italic', fontWeight: 900 }}
                        >
                          I
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => applySpotNotesCommand('insertUnorderedList')}
                          sx={{ minWidth: 32, px: 0.75, fontWeight: 950 }}
                        >
                          •
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInSpotNotes('♠', '#e5e7eb')}
                          sx={{ minWidth: 32, px: 0.75, fontWeight: 950, color: '#e5e7eb' }}
                        >
                          ♠
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInSpotNotes('♥', '#ef4444')}
                          sx={{ minWidth: 32, px: 0.75, fontWeight: 950, color: '#ef4444' }}
                        >
                          ♥
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInSpotNotes('♦', '#3b82f6')}
                          sx={{ minWidth: 32, px: 0.75, fontWeight: 950, color: '#3b82f6' }}
                        >
                          ♦
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInSpotNotes('♣', '#22c55e')}
                          sx={{ minWidth: 32, px: 0.75, fontWeight: 950, color: '#22c55e' }}
                        >
                          ♣
                        </Button>
                      </Stack>

                      <Box
                        ref={spotNotesEditorRef}
                        contentEditable
                        suppressContentEditableWarning
                        onInput={syncSpotNotesContentFromEditor}
                        onBlur={() => void persistCurrentSpotNotes()}
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          border: '1px solid',
                          borderColor: 'rgba(148, 163, 184, 0.28)',
                          borderRadius: 2,
                          p: 1.35,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'rgba(76, 201, 216, 0.65) rgba(15, 23, 42, 0.75)',
                          '&::-webkit-scrollbar': {
                            width: 9,
                          },
                          '&::-webkit-scrollbar-track': {
                            bgcolor: 'rgba(15, 23, 42, 0.75)',
                            borderRadius: 999,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'rgba(76, 201, 216, 0.55)',
                            borderRadius: 999,
                            border: '2px solid rgba(15, 23, 42, 0.75)',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            bgcolor: 'rgba(76, 201, 216, 0.85)',
                          },
                          outline: 'none',
                          typography: 'body2',
                          fontSize: 14,
                          lineHeight: 1.65,
                          color: 'rgba(255,255,255,0.92)',
                          bgcolor: 'rgba(15, 23, 42, 0.38)',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                          '&:focus': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 1px rgba(76, 201, 216, 0.35)',
                          },
                          '& p': {
                            my: 0.5,
                            color: 'rgba(255,255,255,0.92)',
                          },
                          '& ul': {
                            my: 0.5,
                            pl: 2,
                            color: 'rgba(255,255,255,0.92)',
                          },
                          '& li': {
                            color: 'rgba(255,255,255,0.92)',
                          },
                          '& strong': {
                            fontWeight: 900,
                            color: '#ffffff',
                          },
                          '& em': {
                            fontStyle: 'italic',
                            color: 'rgba(255,255,255,0.96)',
                          },
                        }}
                      />
                    </>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        border: '1px solid rgba(148, 163, 184, 0.22)',
                        borderRadius: 2.5,
                        p: 1.35,
                        overflow: 'auto',
                        bgcolor: 'rgba(15, 23, 42, 0.34)',
                      }}
                    >
                      {isLoadingSpotStrategy ? (
                        <Box
                          sx={{
                            minHeight: 260,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CircularProgress size={28} />
                        </Box>
                      ) : !primaryDecisionPoint ? (
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            bgcolor: 'rgba(255,255,255,0.035)',
                            borderColor: 'rgba(76, 201, 216, 0.22)',
                          }}
                        >
                          <CardContent>
                            <Typography variant="caption" color="text.secondary" fontWeight={950}>
                              LÍNEAS DEL SPOT
                            </Typography>

                            <Typography variant="h6" fontWeight={950} sx={{ mt: 0.4 }}>
                              Crea el primer punto de decisión
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                              Empezaremos en flop con el jugador que actúa primero. Después podrás
                              definir líneas estratégicas y asociarlas a acciones y tipos de board.
                            </Typography>

                            <Button
                              variant="contained"
                              onClick={handleCreateFlopDecisionPoint}
                              disabled={isCreatingFlopDecisionPoint}
                              sx={{ mt: 2, borderRadius: 2, fontWeight: 900 }}
                            >
                              {isCreatingFlopDecisionPoint ? 'Creando...' : 'Crear líneas del flop'}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <Stack spacing={1.15}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2.5,
                              bgcolor: 'rgba(2, 6, 23, 0.34)',
                              borderColor: 'rgba(148, 163, 184, 0.18)',
                            }}
                          >
                            <CardContent sx={{ p: 1.2, '&:last-child': { pb: 1.2 } }}>
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={950}
                                  >
                                    LÍNEAS DEL SPOT
                                  </Typography>

                                  <Typography variant="body1" fontWeight={950} sx={{ mt: 0.2 }}>
                                    {primaryDecisionPoint.title ||
                                      `${getStreetLabel(primaryDecisionPoint.street)} · Actúa ${getStrategyActorLabel(
                                        detail.spot,
                                        primaryDecisionPoint.acting_player,
                                      )}`}
                                  </Typography>

                                  {parentDecisionPoint ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.65}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      sx={{ mt: 0.75 }}
                                    >
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Origen: ${parentDecisionPoint.title}`}
                                        sx={{
                                          height: 22,
                                          fontSize: 11,
                                          fontWeight: 850,
                                          maxWidth: 360,
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                        }}
                                      />

                                      {primaryDecisionPoint.trigger_action_label ? (
                                        <Chip
                                          size="small"
                                          color="primary"
                                          label={`Acción: ${primaryDecisionPoint.trigger_action_label}`}
                                          sx={{ height: 22, fontSize: 11, fontWeight: 900 }}
                                        />
                                      ) : null}

                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => setActiveDecisionPointId(parentDecisionPoint.id)}
                                        sx={{
                                          minHeight: 24,
                                          px: 0.5,
                                          fontSize: 11.5,
                                          fontWeight: 900,
                                          textTransform: 'none',
                                        }}
                                      >
                                        ← Volver
                                      </Button>
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      Clasifica los boards del spot por línea estratégica.
                                    </Typography>
                                  )}
                                </Box>

                                <Stack direction="row" spacing={0.6} flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    label={`${primaryDecisionPoint.strategy_lines.length} líneas`}
                                    sx={{ height: 22, fontWeight: 850 }}
                                  />
                                  <Chip
                                    size="small"
                                    label={`${primaryDecisionPoint.strategy_lines.reduce(
                                      (acc, line) => acc + readBoardTypePresetsFromLine(line).length,
                                      0,
                                    )} tipos de board`}
                                    sx={{ height: 22, fontWeight: 850 }}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => openCreateStrategyLineDialog(primaryDecisionPoint)}
                                    disabled={isSavingStrategyLine}
                                    sx={{
                                      minHeight: 28,
                                      borderRadius: 2,
                                      fontWeight: 900,
                                      textTransform: 'none',
                                    }}
                                  >
                                    Nueva línea
                                  </Button>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>

                          {primaryDecisionPoint.strategy_lines.length === 0 ? (
                            <Alert severity="info" variant="outlined">
                              {parentDecisionPoint
                                ? 'Aún no hay líneas de respuesta. Define cómo responde este jugador frente a la acción anterior.'
                                : 'Aún no hay líneas estratégicas. Crea líneas como Bet range, Mixto o Check range y después añade acciones.'}
                            </Alert>
                          ) : null}

                          <Grid container spacing={0.9}>
                            {primaryDecisionPoint.strategy_lines.map((line) => {
                              const boardTypePresets = readBoardTypePresetsFromLine(line);

                              return (
                                <Grid item xs={12} md={4} key={line.id}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      height: '100%',
                                      borderRadius: 2.35,
                                      bgcolor: 'rgba(15, 23, 42, 0.62)',
                                      borderColor: 'rgba(148, 163, 184, 0.16)',
                                      boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                                      transition:
                                        'border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                                      '&:hover': {
                                        borderColor: 'rgba(76, 201, 216, 0.48)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 16px 34px rgba(0,0,0,0.24)',
                                      },
                                    }}
                                  >
                                    <CardContent
                                      sx={{
                                        p: 1.05,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.85,
                                        '&:last-child': { pb: 1.05 },
                                      }}
                                    >
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                      >
                                        <Box sx={{ minWidth: 0 }}>
                                          <Stack
                                            direction="row"
                                            spacing={0.65}
                                            alignItems="center"
                                            flexWrap="wrap"
                                          >
                                            <Typography
                                              fontWeight={950}
                                              sx={{
                                                lineHeight: 1.05,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 190,
                                              }}
                                            >
                                              {line.name}
                                            </Typography>

                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={line.strategy_type || 'general'}
                                              sx={{
                                                height: 18,
                                                maxWidth: '100%',
                                                fontSize: 9.5,
                                                fontWeight: 850,
                                                bgcolor: 'rgba(2, 6, 23, 0.34)',
                                                borderColor: 'rgba(148, 163, 184, 0.28)',
                                              }}
                                            />
                                          </Stack>

                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              display: '-webkit-box',
                                              WebkitLineClamp: 1,
                                              WebkitBoxOrient: 'vertical',
                                              overflow: 'hidden',
                                              mt: 0.55,
                                              lineHeight: 1.25,
                                              minHeight: 16,
                                            }}
                                          >
                                            {line.summary || 'Sin resumen estratégico.'}
                                          </Typography>
                                        </Box>

                                        <Stack direction="row" spacing={0.4} sx={{ flexShrink: 0 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => openEditStrategyLineDialog(line)}
                                            sx={{
                                              minHeight: 23,
                                              px: 0.85,
                                              borderRadius: 1.55,
                                              fontSize: 10.5,
                                              fontWeight: 900,
                                              textTransform: 'none',
                                            }}
                                          >
                                            Editar
                                          </Button>

                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => void handleDeleteStrategyLine(line)}
                                            disabled={isDeletingStrategyLine}
                                            sx={{
                                              minHeight: 23,
                                              px: 0.85,
                                              borderRadius: 1.55,
                                              fontSize: 10.5,
                                              fontWeight: 900,
                                              textTransform: 'none',
                                            }}
                                          >
                                            Eliminar
                                          </Button>
                                        </Stack>
                                      </Stack>

                                      <Box
                                        sx={{
                                          borderRadius: 2,
                                          border: '1px solid rgba(76, 201, 216, 0.14)',
                                          bgcolor: 'rgba(2, 6, 23, 0.28)',
                                          p: 0.85,
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          spacing={1}
                                          sx={{ mb: 0.65 }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={950}
                                          >
                                            TIPOS DE BOARD
                                          </Typography>

                                          <Chip
                                            size="small"
                                            label={boardTypePresets.length}
                                            sx={{
                                              height: 18,
                                              minWidth: 22,
                                              fontSize: 10,
                                              fontWeight: 950,
                                              bgcolor: 'rgba(76, 201, 216, 0.15)',
                                              color: '#a5f3fc',
                                            }}
                                          />
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          spacing={0.45}
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          {boardTypePresets.length === 0 ? (
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label="Sin tipos todavía"
                                              sx={{
                                                height: 22,
                                                fontSize: 10.5,
                                                fontWeight: 850,
                                                opacity: 0.72,
                                              }}
                                            />
                                          ) : null}

                                          {boardTypePresets.map((preset) => (
                                            <Stack
                                              key={preset.id}
                                              direction="row"
                                              spacing={0.25}
                                              alignItems="center"
                                              sx={{
                                                borderRadius: 999,
                                                bgcolor: 'rgba(8, 145, 178, 0.06)',
                                              }}
                                            >
                                              <Chip
                                                size="small"
                                                clickable
                                                variant="outlined"
                                                label={preset.label}
                                                onClick={() => handleApplyBoardTypePreset(line, preset)}
                                                sx={{
                                                  height: 22,
                                                  fontSize: 10.5,
                                                  fontWeight: 900,
                                                  color: '#cffafe',
                                                  bgcolor: 'rgba(8, 145, 178, 0.08)',
                                                  borderColor: 'rgba(103, 232, 249, 0.32)',
                                                  '&:hover': {
                                                    bgcolor: 'rgba(8, 145, 178, 0.16)',
                                                    borderColor: 'rgba(103, 232, 249, 0.58)',
                                                  },
                                                }}
                                              />

                                              <Chip
                                                size="small"
                                                clickable
                                                variant="outlined"
                                                label="Editar"
                                                onClick={() => openEditBoardTypeDialog(line, preset)}
                                                sx={{
                                                  height: 20,
                                                  fontSize: 9.5,
                                                  fontWeight: 900,
                                                  borderColor: 'rgba(148, 163, 184, 0.28)',
                                                }}
                                              />

                                              <Chip
                                                size="small"
                                                clickable
                                                variant="outlined"
                                                label="×"
                                                onClick={() => void handleDeleteBoardTypePreset(line, preset)}
                                                sx={{
                                                  height: 20,
                                                  minWidth: 22,
                                                  fontSize: 12,
                                                  fontWeight: 950,
                                                  color: '#fecaca',
                                                  borderColor: 'rgba(248, 113, 113, 0.38)',
                                                }}
                                              />
                                            </Stack>
                                          ))}

                                          <Chip
                                            size="small"
                                            clickable
                                            variant="outlined"
                                            label="+ Tipo board"
                                            onClick={() => openBoardTypeDialog(line)}
                                            sx={{
                                              height: 22,
                                              fontSize: 10.5,
                                              fontWeight: 950,
                                              color: '#67e8f9',
                                              borderColor: 'rgba(103, 232, 249, 0.44)',
                                            }}
                                          />
                                        </Stack>
                                      </Box>

                                      <Box>
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          spacing={1}
                                          sx={{ mb: 0.55 }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={950}
                                          >
                                            ACCIONES
                                          </Typography>

                                          <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => openStrategyLineActionDialog(line)}
                                            sx={{
                                              minHeight: 22,
                                              px: 1,
                                              borderRadius: 1.6,
                                              fontSize: 10.5,
                                              fontWeight: 950,
                                              textTransform: 'none',
                                            }}
                                          >
                                            + Acción
                                          </Button>
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          spacing={0.4}
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          {line.actions.length === 0 ? (
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label="Sin acciones"
                                              sx={{ height: 22, fontSize: 10.5, fontWeight: 850 }}
                                            />
                                          ) : null}

                                          {line.actions.map((action) => (
                                            <Stack
                                              key={action.id}
                                              direction="row"
                                              spacing={0.25}
                                              alignItems="center"
                                            >
                                              <Chip
                                                size="small"
                                                label={action.label}
                                                sx={{
                                                  height: 23,
                                                  fontSize: 10.5,
                                                  fontWeight: 950,
                                                  color: '#0f172a',
                                                  bgcolor: getTheoryActionColor(
                                                    action.action_type,
                                                    action.size_percentage,
                                                  ),
                                                  '& .MuiChip-label': {
                                                    px: 0.8,
                                                  },
                                                }}
                                              />

                                              <Chip
                                                size="small"
                                                clickable
                                                variant="outlined"
                                                disabled={openingResponseActionId === action.id}
                                                label={
                                                  openingResponseActionId === action.id
                                                    ? '...'
                                                    : '→'
                                                }
                                                onClick={() =>
                                                  void handleOpenResponseDecisionPoint(action.id)
                                                }
                                                sx={{
                                                  height: 23,
                                                  minWidth: 24,
                                                  fontWeight: 950,
                                                  borderColor: 'rgba(148, 163, 184, 0.34)',
                                                }}
                                              />
                                            </Stack>
                                          ))}
                                        </Stack>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={3}>
              <Card
                variant="outlined"
                sx={{
                  height: { xs: 'auto', lg: 'clamp(640px, calc(100vh - 245px), 760px)' },
                  maxHeight: { xs: 'none', lg: 'clamp(640px, calc(100vh - 245px), 760px)' },
                  minHeight: { xs: 420, lg: 0 },
                  borderRadius: 3,
                  bgcolor: 'rgba(15, 23, 42, 0.2)',
                  borderColor: 'rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.14)',
                  overflow: 'hidden',
                }}
              >
                <CardContent
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '&:last-child': { pb: 1.5 },
                  }}
                >
                  <Stack spacing={1.15} sx={{ mb: 1.5, flexShrink: 0 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={950}>
                          BOARDS
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {filteredBoards.length}/{totalBoards} flops
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={openBoardDialog}
                        sx={{ fontWeight: 900, borderRadius: 2, px: 1.4 }}
                      >
                        Añadir
                      </Button>
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 0.75,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.75,
                          bgcolor: 'rgba(255,255,255,0.035)',
                          fontSize: 12,
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: 12,
                          fontWeight: 850,
                        },
                        '& .MuiSelect-select': {
                          py: 0.75,
                        },
                      }}
                    >
                      <TextField
                        select
                        size="small"
                        label="High"
                        value={boardHighCardFilter}
                        onChange={(event) => setBoardHighCardFilter(event.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        {boardHighCardOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}-high
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Pair"
                        value={boardPairedFilter}
                        onChange={(event) => setBoardPairedFilter(event.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        {boardPairedOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Broadways"
                        value={boardBroadwayFilter}
                        onChange={(event) => setBoardBroadwayFilter(event.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="0">0</MenuItem>
                        <MenuItem value="1">1</MenuItem>
                        <MenuItem value="2">2</MenuItem>
                        <MenuItem value="3">3</MenuItem>
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Suit"
                        value={boardSuitedFilter}
                        onChange={(event) => setBoardSuitedFilter(event.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        {boardSuitedOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Conn"
                        value={boardConnectivityFilter}
                        onChange={(event) => setBoardConnectivityFilter(event.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="connected">connected</MenuItem>
                        <MenuItem value="two_gap">two_gap</MenuItem>
                        <MenuItem value="one_gap">one_gap</MenuItem>
                        <MenuItem value="disconnected">disconnected</MenuItem>
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Straight"
                        value={boardStraightFilter}
                        onChange={(event) => setBoardStraightFilter(event.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="yes">Sí</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </TextField>
                    </Box>

                    {hasActiveBoardFilters ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        {activeBoardTypeLabel ? (
                          <Chip
                            size="small"
                            color="primary"
                            label={`Tipo de board · ${activeBoardTypeLabel}`}
                            sx={{ height: 22, fontWeight: 900 }}
                          />
                        ) : null}

                        <Chip
                          size="small"
                          label="Limpiar filtros"
                          variant="outlined"
                          onClick={clearBoardFilters}
                          sx={{ height: 22, fontWeight: 850 }}
                        />
                      </Stack>
                    ) : null}
                  </Stack>

                  {totalBoards === 0 ? (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.035)',
                        borderColor: 'rgba(148, 163, 184, 0.16)',
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight={850}>
                          Todavía no hay boards
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Crea el primer board para empezar a estudiar líneas y rangos.
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : filteredBoards.length === 0 ? (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.035)',
                        borderColor: 'rgba(148, 163, 184, 0.16)',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" fontWeight={850} gutterBottom>
                          Ningún board coincide
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                          Ajusta los filtros para volver a mostrar boards.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={clearBoardFilters}
                        >
                          Limpiar filtros
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(76, 201, 216, 0.65) rgba(15, 23, 42, 0.75)',
                        pr: 0.5,
                        pb: 0.5,
                        '&::-webkit-scrollbar': {
                          width: 9,
                        },
                        '&::-webkit-scrollbar-track': {
                          bgcolor: 'rgba(15, 23, 42, 0.75)',
                          borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          bgcolor: 'rgba(76, 201, 216, 0.55)',
                          borderRadius: 999,
                          border: '2px solid rgba(15, 23, 42, 0.75)',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          bgcolor: 'rgba(76, 201, 216, 0.85)',
                        },
                      }}
                    >
                      <Stack spacing={0.75}>
                        {filteredBoards.map((board) => (
                          <Card
                            key={board.id}
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 2.25,
                              bgcolor: 'rgba(255,255,255,0.035)',
                              borderColor: 'rgba(148, 163, 184, 0.16)',
                              transition:
                                'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.055)',
                                borderColor: 'rgba(76, 201, 216, 0.42)',
                                boxShadow:
                                  'inset 0 0 0 1px rgba(76, 201, 216, 0.24), 0 14px 28px rgba(15, 23, 42, 0.24)',
                              },
                            }}
                            onClick={() => navigate(`/poker-study/boards/${board.id}`)}
                          >
                            <CardContent sx={{ p: 0.9, '&:last-child': { pb: 0.9 } }}>
                              <Stack spacing={0.7}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  {board.board_mode === 'exact_board' ? (
                                    <Stack direction="row" spacing={0.4}>
                                      <SpotPokerCard value={board.flop_card_1} compact />
                                      <SpotPokerCard value={board.flop_card_2} compact />
                                      <SpotPokerCard value={board.flop_card_3} compact />
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" fontWeight={900} noWrap>
                                      {buildBoardLabel(board)}
                                    </Typography>
                                  )}

                                  <Stack direction="row" spacing={0.5} flexShrink={0}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openEditBoardDialog(board);
                                      }}
                                      sx={{
                                        minHeight: 23,
                                        px: 0.75,
                                        fontSize: 10.5,
                                        fontWeight: 850,
                                        borderRadius: 1.35,
                                      }}
                                    >
                                      Editar
                                    </Button>

                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setBoardPendingDelete(board);
                                      }}
                                      sx={{
                                        minHeight: 23,
                                        px: 0.75,
                                        fontSize: 10.5,
                                        fontWeight: 850,
                                        borderRadius: 1.35,
                                      }}
                                    >
                                      Eliminar
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      )}

      <Dialog
        open={strategyLineDialogOpen}
        onClose={closeStrategyLineDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            {editingStrategyLine ? 'EDITAR LÍNEA' : 'NUEVA LÍNEA'}
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {editingStrategyLine ? editingStrategyLine.name : 'Crear línea estratégica'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                Presets rápidos
              </Typography>

              <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 0.75 }}>
                {getStrategyLinePresetsForDecisionPoint(selectedDecisionPoint).map((preset) => (
                  <Button
                    key={preset.label}
                    size="small"
                    variant={
                      strategyLineForm.name === preset.label ? 'contained' : 'outlined'
                    }
                    onClick={() =>
                      setStrategyLineForm((current) => ({
                        ...current,
                        name: preset.label,
                        strategy_type: preset.strategy_type,
                        summary: preset.summary,
                      }))
                    }
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Stack>
            </Box>

            <TextField
              label="Nombre de la línea"
              value={strategyLineForm.name}
              onChange={(event) =>
                setStrategyLineForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              placeholder="Ej. Bet range, Mixto, Check range"
              fullWidth
            />

            <TextField
              select
              label="Tipo de línea"
              value={strategyLineForm.strategy_type}
              onChange={(event) =>
                setStrategyLineForm((current) => ({
                  ...current,
                  strategy_type: event.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="range_bet">Bet range</MenuItem>
              <MenuItem value="mixed">Mixto</MenuItem>
              <MenuItem value="range_check">Check range</MenuItem>
              <MenuItem value="exploit">Exploit / adaptación</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </TextField>

            <TextField
              label="Resumen"
              value={strategyLineForm.summary}
              onChange={(event) =>
                setStrategyLineForm((current) => ({
                  ...current,
                  summary: event.target.value,
                }))
              }
              placeholder="Ej. Línea para boards donde SB mantiene ventaja clara de rango."
              multiline
              minRows={2}
              fullWidth
            />

            <TextField
              label="Notas"
              value={strategyLineForm.notes}
              onChange={(event) =>
                setStrategyLineForm((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeStrategyLineDialog}
            disabled={isSavingStrategyLine}
            sx={{ fontWeight: 900 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleSubmitStrategyLine()}
            disabled={isSavingStrategyLine}
            sx={{ fontWeight: 900, borderRadius: 2 }}
          >
            {isSavingStrategyLine
              ? 'Guardando...'
              : editingStrategyLine
                ? 'Guardar cambios'
                : 'Crear línea'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={strategyLineActionDialogOpen}
        onClose={closeStrategyLineActionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            AÑADIR ACCIÓN
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {selectedStrategyLineForAction
              ? selectedStrategyLineForAction.name
              : 'Línea estratégica'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                Acciones rápidas
              </Typography>

              <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 0.75 }}>
                {STRATEGY_ACTION_PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    size="small"
                    variant={
                      strategyLineActionForm.label === preset.label ? 'contained' : 'outlined'
                    }
                    onClick={() =>
                      setStrategyLineActionForm((current) => ({
                        ...current,
                        action_type: preset.action_type,
                        size_percentage: preset.size_percentage,
                        label: preset.label,
                      }))
                    }
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Stack>
            </Box>

            <TextField
              select
              label="Acción"
              value={strategyLineActionForm.action_type}
              onChange={(event) =>
                setStrategyLineActionForm((current) => ({
                  ...current,
                  action_type: event.target.value as StrategyLineActionFormState['action_type'],
                }))
              }
              fullWidth
            >
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="bet">Bet</MenuItem>
              <MenuItem value="call">Call</MenuItem>
              <MenuItem value="raise">Raise</MenuItem>
              <MenuItem value="fold">Fold</MenuItem>
              <MenuItem value="other">Otra</MenuItem>
            </TextField>

            {strategyLineActionForm.action_type === 'bet' ||
            strategyLineActionForm.action_type === 'raise' ? (
              <TextField
                label="Tamaño"
                value={strategyLineActionForm.size_percentage}
                onChange={(event) =>
                  setStrategyLineActionForm((current) => ({
                    ...current,
                    size_percentage: event.target.value,
                  }))
                }
                placeholder="Ej. 25"
                inputProps={{ inputMode: 'decimal' }}
                fullWidth
              />
            ) : null}

            <TextField
              label="Etiqueta visible"
              value={strategyLineActionForm.label}
              onChange={(event) =>
                setStrategyLineActionForm((current) => ({
                  ...current,
                  label: event.target.value,
                }))
              }
              placeholder="Opcional. Ej. Bet 25, Bet grande, Check"
              fullWidth
            />

            <TextField
              label="Peso / frecuencia"
              value={strategyLineActionForm.weight_label}
              onChange={(event) =>
                setStrategyLineActionForm((current) => ({
                  ...current,
                  weight_label: event.target.value,
                }))
              }
              placeholder="Opcional. Ej. principal, mix, baja frecuencia"
              fullWidth
            />

            <TextField
              label="Notas"
              value={strategyLineActionForm.notes}
              onChange={(event) =>
                setStrategyLineActionForm((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeStrategyLineActionDialog}
            disabled={isSavingStrategyLineAction}
            sx={{ fontWeight: 900 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleSubmitStrategyLineAction()}
            disabled={isSavingStrategyLineAction}
            sx={{ fontWeight: 900, borderRadius: 2 }}
          >
            {isSavingStrategyLineAction ? 'Guardando...' : 'Añadir acción'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={strategyBoardFamilyDialogOpen}
        onClose={closeStrategyBoardFamilyDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            ASIGNAR TIPO DE BOARD
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {selectedStrategyLineForBoardFamily
              ? selectedStrategyLineForBoardFamily.name
              : 'Línea estratégica'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                Tipos frecuentes
              </Typography>

              <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 0.75 }}>
                {BOARD_FAMILY_PRESETS.map((preset) => (
                  <Button
                    key={preset.title}
                    size="small"
                    variant={
                      strategyBoardFamilyForm.title === preset.title ? 'contained' : 'outlined'
                    }
                    onClick={() =>
                      setStrategyBoardFamilyForm((current) => ({
                        ...current,
                        title: preset.title,
                        description: preset.description,
                        tags_text: preset.tags_text,
                      }))
                    }
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    {preset.title}
                  </Button>
                ))}
              </Stack>
            </Box>

            <TextField
              label="Tipo / familia de board"
              value={strategyBoardFamilyForm.title}
              onChange={(event) =>
                setStrategyBoardFamilyForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="Ej. 2 broadways con/sin FD, board bajo, monocolor"
              fullWidth
            />

            <TextField
              label="Descripción"
              value={strategyBoardFamilyForm.description}
              onChange={(event) =>
                setStrategyBoardFamilyForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              placeholder="Qué caracteriza a esta familia y por qué pertenece a esta línea."
              multiline
              minRows={3}
              fullWidth
            />

            <TextField
              label="Ejemplos"
              value={strategyBoardFamilyForm.examples_text}
              onChange={(event) =>
                setStrategyBoardFamilyForm((current) => ({
                  ...current,
                  examples_text: event.target.value,
                }))
              }
              placeholder="Ej. KQ4r, KJ7ss, QJ2r"
              multiline
              minRows={2}
              fullWidth
            />

            <TextField
              label="Tags"
              value={strategyBoardFamilyForm.tags_text}
              onChange={(event) =>
                setStrategyBoardFamilyForm((current) => ({
                  ...current,
                  tags_text: event.target.value,
                }))
              }
              placeholder="Separados por comas. Ej. broadway, range-bet, two-tone"
              fullWidth
            />

            <TextField
              label="Notas"
              value={strategyBoardFamilyForm.notes}
              onChange={(event) =>
                setStrategyBoardFamilyForm((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeStrategyBoardFamilyDialog}
            disabled={isSavingStrategyBoardFamily}
            sx={{ fontWeight: 900 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleSubmitStrategyBoardFamily()}
            disabled={isSavingStrategyBoardFamily}
            sx={{ fontWeight: 900, borderRadius: 2 }}
          >
            {isSavingStrategyBoardFamily ? 'Guardando...' : 'Asignar tipo'}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={Boolean(selectedRangeSlot)}
        onClose={closePreflopRangeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background:
              'linear-gradient(135deg, rgba(34,197,94,0.16), rgba(15,23,42,0.1) 55%, rgba(2,6,23,0.2))',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                RANGO PREFLOP
              </Typography>
              <Typography variant="h6" fontWeight={950}>
                {selectedRangeSlot?.title || 'Rango'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedRangeSlot
                  ? `${formatPosition(selectedRangeSlot.position)} · ${selectedRangeSlot.subtitle}`
                  : ''}
              </Typography>
            </Box>

            <Chip
              size="small"
              label={`${getPreflopRangeComboCount(preflopRangeDraft)} combos · ${formatPreflopRangePercentage(
                getPreflopRangeComboCount(preflopRangeDraft),
              )}`}
              sx={{
                fontWeight: 900,
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                border: '1px solid rgba(34, 197, 94, 0.32)',
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'rgba(15, 23, 42, 0.18)' }}>
          <Stack spacing={1.5} sx={{ pt: 1.5 }}>
            {errorMessage && selectedRangeSlot ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}

            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.035)',
                borderColor: 'rgba(148, 163, 184, 0.16)',
              }}
            >
              <CardContent sx={{ p: 1.35, '&:last-child': { pb: 1.35 } }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={950}>
                      IMPORTAR PLANTILLA
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Copia una plantilla compatible al draft actual. Después debes guardar el rango del spot.
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  >
                    {isLoadingRangeTemplates ? (
                      <Chip
                        size="small"
                        label="Cargando plantillas..."
                        sx={{ fontWeight: 900 }}
                      />
                    ) : compatibleRangeTemplates.length === 0 ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Sin plantillas compatibles"
                        sx={{ fontWeight: 900 }}
                      />
                    ) : (
                      compatibleRangeTemplates.slice(0, 6).map((template) => {
                        const templateHandClasses = getRangeTemplateHandClasses(template);
                        const comboCount = getPreflopRangeComboCount(templateHandClasses);

                        return (
                          <Button
                            key={template.id}
                            size="small"
                            variant="outlined"
                            onClick={() => importTemplateIntoPreflopDraft(template)}
                            sx={{
                              minHeight: 30,
                              borderRadius: 1.75,
                              px: 1.15,
                              fontSize: 11.5,
                              fontWeight: 900,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              textTransform: 'none',
                              borderColor: 'rgba(34, 197, 94, 0.36)',
                              bgcolor: 'rgba(34, 197, 94, 0.07)',
                              '&:hover': {
                                bgcolor: 'rgba(34, 197, 94, 0.13)',
                                borderColor: 'rgba(34, 197, 94, 0.58)',
                              },
                            }}
                          >
                            {template.name} · {comboCount} combos
                          </Button>
                        );
                      })
                    )}
                  </Stack>
                </Stack>

                {compatibleRangeTemplates.length > 6 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Mostrando las 6 primeras plantillas compatibles. Ajustaremos buscador/importador avanzado más adelante.
                  </Typography>
                ) : null}
              </CardContent>
            </Card>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(13, 42px)',
                gap: 0.45,
                justifyContent: 'center',
                p: 1.25,
                borderRadius: 3,
                bgcolor: 'rgba(15, 23, 42, 0.42)',
                border: '1px solid rgba(148, 163, 184, 0.16)',
                overflowX: 'auto',
              }}
            >
              {buildPreflopRangeMatrix().flat().map((handClass) => {
                const isSelected = preflopRangeDraft.has(handClass);

                return (
                  <Box
                    key={handClass}
                    onMouseDown={(event) => {
                      if (event.button !== 0) return;
                      event.preventDefault();
                      startPreflopRangePaint(handClass);
                    }}
                    onMouseEnter={() => continuePreflopRangePaint(handClass)}
                    sx={{
                      width: 42,
                      height: 34,
                      borderRadius: isPocketPairHandClass(handClass) ? 1.6 : 1.15,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: 12,
                      fontWeight: isPocketPairHandClass(handClass) ? 1000 : 900,
                      color: isSelected ? '#052e16' : 'rgba(255,255,255,0.88)',
                      bgcolor: isSelected
                        ? '#22c55e'
                        : isPocketPairHandClass(handClass)
                          ? 'rgba(148, 163, 184, 0.12)'
                          : 'rgba(255,255,255,0.035)',
                      border: '1px solid',
                      borderColor: isSelected
                        ? 'rgba(134, 239, 172, 0.95)'
                        : isPocketPairHandClass(handClass)
                          ? 'rgba(226, 232, 240, 0.34)'
                          : 'rgba(148, 163, 184, 0.18)',
                      boxShadow: isSelected
                        ? '0 0 0 1px rgba(34, 197, 94, 0.22), 0 8px 18px rgba(34, 197, 94, 0.12)'
                        : isPocketPairHandClass(handClass)
                          ? 'inset 0 0 0 1px rgba(255,255,255,0.05)'
                          : 'none',
                      outline: isPocketPairHandClass(handClass)
                        ? '1px solid rgba(255,255,255,0.08)'
                        : 'none',
                      transition:
                        'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
                      '&:hover': {
                        borderColor: isSelected ? '#bbf7d0' : 'primary.main',
                        bgcolor: isSelected ? '#4ade80' : 'rgba(76, 201, 216, 0.12)',
                      },
                    }}
                  >
                    {handClass}
                  </Box>
                );
              })}
            </Box>

            <Typography variant="caption" color="text.secondary" textAlign="center">
              Arrastra para pintar o limpiar. V1 guarda clases de mano completas, mostrando combos y porcentaje real del rango.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.92)',
          }}
        >
          <Button
            onClick={() => setPreflopRangeDraft(new Set())}
            disabled={isSavingPreflopRange || preflopRangeDraft.size === 0}
          >
            Limpiar
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button onClick={closePreflopRangeDialog} disabled={isSavingPreflopRange}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSavePreflopRange}
            disabled={isSavingPreflopRange || !selectedRangeSlot}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              px: 2.4,
              boxShadow: '0 12px 28px rgba(34, 197, 94, 0.18)',
            }}
          >
            {isSavingPreflopRange ? 'Guardando...' : 'Guardar rango'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={boardFilterPresetDialogOpen}
        onClose={closeBoardFilterPresetDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.98)',
            border: '1px solid rgba(76, 201, 216, 0.24)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.48)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.6,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.38)',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            {boardTypeDialogMode === 'edit' ? 'EDITAR TIPO DE BOARD' : 'NUEVO TIPO DE BOARD'}
          </Typography>

          <Typography variant="h6" fontWeight={950} sx={{ lineHeight: 1.15 }}>
            {selectedBoardTypeLine ? selectedBoardTypeLine.name : 'Nueva línea'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
            Define qué textura de board encaja con esta línea estratégica.
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2,
            py: 1.6,
            bgcolor: 'rgba(15, 23, 42, 0.22)',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.035)',
            },
            '& .MuiInputLabel-root': {
              fontWeight: 850,
            },
          }}
        >
          <Stack spacing={1.25}>
            <Box
              sx={{
                borderRadius: 2.25,
                border: '1px solid rgba(76, 201, 216, 0.16)',
                bgcolor: 'rgba(2, 6, 23, 0.24)',
                p: 1.15,
              }}
            >
              <TextField
                label="Nombre del tipo"
                value={boardFilterPresetForm.label}
                onChange={(event) =>
                  setBoardFilterPresetForm((current) => ({
                    ...current,
                    label: event.target.value,
                  }))
                }
                placeholder={buildBoardTypeTitleFromForm(boardFilterPresetForm)}
                helperText={`Predeterminado: ${buildBoardTypeTitleFromForm(boardFilterPresetForm)}`}
                fullWidth
              />
            </Box>

            <Box
              sx={{
                borderRadius: 2.25,
                border: '1px solid rgba(148, 163, 184, 0.16)',
                bgcolor: 'rgba(2, 6, 23, 0.22)',
                p: 1.15,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={950}
                sx={{ display: 'block', mb: 1 }}
              >
                CRITERIOS DEL BOARD
              </Typography>

              <Grid container spacing={1.1}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="High"
                    value={boardFilterPresetForm.high_card_rank}
                    onChange={(event) =>
                      setBoardFilterPresetForm((current) => ({
                        ...current,
                        high_card_rank: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="A">A-high</MenuItem>
                    <MenuItem value="K">K-high</MenuItem>
                    <MenuItem value="Q">Q-high</MenuItem>
                    <MenuItem value="J">J-high</MenuItem>
                    <MenuItem value="T">T-high</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Pair"
                    value={boardFilterPresetForm.paired_status}
                    onChange={(event) =>
                      setBoardFilterPresetForm((current) => ({
                        ...current,
                        paired_status: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="unpaired">Sin pareja</MenuItem>
                    <MenuItem value="paired">Emparejado</MenuItem>
                    <MenuItem value="trips_board">Trips</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Broadways"
                    value={boardFilterPresetForm.broadway_count}
                    onChange={(event) =>
                      setBoardFilterPresetForm((current) => ({
                        ...current,
                        broadway_count: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="0">0</MenuItem>
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3">3</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Suit"
                    value={boardFilterPresetForm.suited_texture}
                    onChange={(event) =>
                      setBoardFilterPresetForm((current) => ({
                        ...current,
                        suited_texture: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="rainbow">Rainbow</MenuItem>
                    <MenuItem value="two_tone">Two-tone</MenuItem>
                    <MenuItem value="monotone">Monotone</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Conn"
                    value={boardFilterPresetForm.connectivity_texture}
                    onChange={(event) =>
                      setBoardFilterPresetForm((current) => ({
                        ...current,
                        connectivity_texture: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="connected">Connected</MenuItem>
                    <MenuItem value="two_gap">Two-gap</MenuItem>
                    <MenuItem value="one_gap">One-gap</MenuItem>
                    <MenuItem value="disconnected">Disconnected</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Straight"
                    value={boardFilterPresetForm.straight_possible}
                    onChange={(event) =>
                      setBoardFilterPresetForm((current) => ({
                        ...current,
                        straight_possible: event.target.value,
                      }))
                    }
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="yes">Sí</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.35,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.28)',
          }}
        >
          <Button
            onClick={closeBoardFilterPresetDialog}
            disabled={isSavingBoardFilterPreset}
            sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmitBoardFilterPreset()}
            disabled={isSavingBoardFilterPreset}
            sx={{
              borderRadius: 2,
              fontWeight: 950,
              textTransform: 'none',
              px: 2,
            }}
          >
            {isSavingBoardFilterPreset
              ? 'Guardando...'
              : boardTypeDialogMode === 'edit'
                ? 'Actualizar tipo'
                : 'Guardar tipo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(boardPendingDelete)}
        onClose={() => {
          if (!isDeletingBoard) {
            setBoardPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(248, 113, 113, 0.28)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="error" fontWeight={950}>
            ELIMINAR BOARD
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {boardPendingDelete ? buildBoardLabel(boardPendingDelete) : 'Board seleccionado'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción eliminará el board y su workspace asociado. No continúes si solo quieres corregir las cartas.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Si confirmas, se borrará este board del spot.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setBoardPendingDelete(null)}
            disabled={isDeletingBoard}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteBoard}
            disabled={isDeletingBoard}
            sx={{ fontWeight: 900 }}
          >
            {isDeletingBoard ? 'Eliminando...' : 'Eliminar board'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isBoardDialogOpen}
        onClose={closeBoardDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background:
              'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(15,23,42,0.1) 55%, rgba(2,6,23,0.2))',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            {boardDialogMode === 'edit' ? 'EDITAR BOARD' : 'NUEVO BOARD'}
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            Selección visual de flop
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: 'rgba(15, 23, 42, 0.18)',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.25,
              bgcolor: 'rgba(255,255,255,0.035)',
            },
            '& .MuiInputLabel-root': {
              fontWeight: 800,
            },
          }}
        >
          <Stack spacing={1.5} sx={{ pt: 1.5 }}>
            {errorMessage && isBoardDialogOpen ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}

            <TextField
              select
              label="Modo"
              value={boardForm.board_mode}
              onChange={(event) =>
                setBoardForm((current) => ({
                  ...current,
                  board_mode: event.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="exact_board">Exact board</MenuItem>
              <MenuItem value="board_family">Board family</MenuItem>
            </TextField>

            {boardForm.board_mode === 'exact_board' ? (
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.035)',
                  borderColor: 'rgba(148, 163, 184, 0.18)',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1.35}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={950}>
                        FLOP
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Selecciona tres cartas. La clasificación se calcula en backend.
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent="center">
                      {(['flop_card_1', 'flop_card_2', 'flop_card_3'] as const).map((field, index) => (
                        <Box
                          key={field}
                          onClick={() => setBoardCardPickerTarget(field)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2.25,
                            p: 0.8,
                            border: '1px solid',
                            borderColor:
                              boardCardPickerTarget === field
                                ? 'primary.main'
                                : 'rgba(148, 163, 184, 0.22)',
                            bgcolor:
                              boardCardPickerTarget === field
                                ? 'rgba(99, 102, 241, 0.16)'
                                : 'rgba(255,255,255,0.025)',
                            transition:
                              'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              borderColor: 'primary.main',
                            },
                          }}
                        >
                          <Stack spacing={0.45} alignItems="center">
                            <Typography variant="caption" color="text.secondary" fontWeight={900}>
                              {index + 1}
                            </Typography>
                            <SpotPokerCard value={boardForm[field]} />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>

                    {boardCardPickerTarget ? (
                      <Box
                        sx={{
                          borderRadius: 2.5,
                          p: 1,
                          bgcolor: 'rgba(15, 23, 42, 0.38)',
                          border: '1px solid rgba(148, 163, 184, 0.16)',
                        }}
                      >
                        <SpotCardPickerGrid
                          selectedCards={[
                            boardForm.flop_card_1,
                            boardForm.flop_card_2,
                            boardForm.flop_card_3,
                          ]}
                          onSelectCard={(card) => {
                            const target = boardCardPickerTarget;
                            setBoardForm((current) => ({
                              ...current,
                              [target]: card,
                            }));

                            if (target === 'flop_card_1') {
                              setBoardCardPickerTarget('flop_card_2');
                            } else if (target === 'flop_card_2') {
                              setBoardCardPickerTarget('flop_card_3');
                            } else {
                              setBoardCardPickerTarget(null);
                            }
                          }}
                        />
                      </Box>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <TextField
                label="Etiqueta familia"
                value={boardForm.board_family_label}
                onChange={(event) =>
                  setBoardForm((current) => ({
                    ...current,
                    board_family_label: event.target.value,
                  }))
                }
                fullWidth
              />
            )}

            <TextField
              label="Tags"
              value={boardForm.board_tags_text}
              onChange={(event) =>
                setBoardForm((current) => ({
                  ...current,
                  board_tags_text: event.target.value,
                }))
              }
              fullWidth
              helperText="Separados por comas. Ejemplo: range bet, check-heavy, paired."
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.6,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.92)',
          }}
        >
          <Button onClick={closeBoardDialog} disabled={isSubmittingBoard}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitBoard}
            variant="contained"
            disabled={isSubmittingBoard}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              px: 2.4,
              boxShadow: '0 12px 28px rgba(79, 70, 229, 0.28)',
            }}
          >
            {isSubmittingBoard
              ? boardDialogMode === 'edit'
                ? 'Guardando...'
                : 'Creando...'
              : boardDialogMode === 'edit'
                ? 'Guardar cambios'
                : 'Crear board'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}